import { prayerThemeInput, captureOutboundOwnerBinding, outboundOwnerStillCurrent } from '@/lib/outbound-prayer-authorization';
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import {
  whoOf,
  statusOf,
  colorFor,
  cardSummary,
  formatShortDate,
  formatShortDateTime,
  formatFullDate,
  formatFullDateTime,
  prayersForWho,
  extractThemes,
  isSelf,
  isPraise,
  findAliasCandidate,
  aliasPairKey,
} from '@/lib/prayer-utils';
import { getVineX, vineRand } from '@/lib/vine-math';
import { interleaveBucketMarkers, type VineItem, type Bucket } from '@/lib/garden-buckets';
import {
  boundPrayerSearchInput,
  PRAYER_SEARCH_MAX_QUERY_LENGTH,
  prayerSearchContextKind,
  prayerSearchIndexSteps,
  runStepsInSlices,
  searchPrayerIndex,
  type PrayerSearchIndex,
  type PrayerSearchMatchKind,
} from '@/lib/search-prayers';
import {
  buildPeopleSearchIndex,
  searchPeopleIndexWithMeta,
  type PeopleArchiveEntry,
  type PeopleSearchFilter,
} from '@/lib/people-search';
import { expandQueryToSynonymTerms } from '@/lib/topic-search';
import {
  boundDiscoverySearchInput,
  DISCOVERY_SEARCH_MAX_QUERY_LENGTH,
} from '@/lib/discovery-search';
import { generateThemeClusters, themeInputKey, type ThemeCluster } from '@/lib/grok';
import { shareAriaForPrayer } from '@/lib/share-aria';
import { useAuth } from '@/hooks/useAuth';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { useOnlineStore, getOnline } from '@/lib/online-store';
import { isCrisisPrayer } from '@/lib/spine-crisis';
import { resolveSavedVerses, getVersesForPrayer } from '@/lib/verse-result';
import { VerseSuggestError, type SuggestedVerse } from '@/lib/verse-suggest';
import { VERSE_SUGGEST_ENABLED } from '@/lib/feature-flags';
import ScriptureVersesSheet from '@/components/ScriptureVersesSheet/ScriptureVersesSheet';
import { ICON_MAP } from './BotanicalIconMap';
import PrivateBadge from './PrivateBadge';
import UpdateSheet from './UpdateSheet';
import SendGlyph from './SendGlyph';
import MergePersonSheet, { type MergeResult } from './MergePersonSheet/MergePersonSheet';
import MergeUndoToast from './MergeUndoToast/MergeUndoToast';
import { CR_CONTAINMENT_ACTIVE } from '@/lib/cr-containment';
import {
  KEEPSAKES_ENABLED,
  enhancedVoiceAllowed,
  isCoreReadOnly,
} from '@/lib/core-cutover';
import { isAliasCandidateEligible } from '@/lib/who-match';
import { useSettingsStore, canonicalizeWho } from '@/lib/settings-store';
import { useMergeUndoToast } from '@/hooks/useMergeUndoToast';
import { useNavLayer } from '@/hooks/useNavLayer';
import { useScrollFreezeRecovery } from '@/hooks/useScrollFreezeRecovery';
import { useAdiaStore } from '@/lib/store';
import { useAudioQueueStore } from '@/lib/audio-queue-store';
import { legacyGroupingNotice } from '@/lib/legacy-grouping-notice';
import { getAuthEpoch } from '@/lib/keepsake/epochs';
import {
  gardenCoordinateRange,
  gardenLastAtOrBefore,
  gardenVisibleRange,
} from '@/lib/garden-virtualization';
import styles from './Garden.module.css';

interface GardenProps {
  prayers: Prayer[];
  onClose: () => void;
  onPrayerTap: (id: string) => void;
  onOpenSummary: () => void;
  onOpenPerson?: (who: string) => void;
  /** Fires only for non-private prayers; private taps surface a local toast instead. */
  onShareCard?: (prayer: Prayer) => void;
  /** Coordinator-computed z for the `garden` layer root (A1). */
  zIndex?: number;
}

type Filter = 'all' | 'moving' | 'answered';
type PeopleFilter = PeopleSearchFilter;
type Layout = 'vine' | 'grid';
type TFn = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Vertical slot per vine node (bounded layout classes, rule #39 as revised
 * by docs/plans/garden/garden-node-density-2026-08-25.md): every node
 * occupies its CLASS's pre-measured slot, so the visible gap between cards
 * is `slot - cardHeight` with a target ~32 px breath. The old single
 * worst-case pitch (507-564 px) reserved a wrapping name + 4-line summary
 * + every badge for EVERY card, leaving ~300 px of dead air under a
 * typical one-line card; three classes close that without reopening
 * measured-height virtualization.
 *
 * Classes (data-classified, NEVER DOM-measured; the CSS line clamps in
 * Garden.module.css ENFORCE each class's name/summary ceilings so a
 * misclassification degrades to an early ellipsis, never overlap):
 *   tall     — any badge row present (isPrivate / pendingTranscriptionId).
 *              Name clamp 2, summary clamp 4, badge rows reserved.
 *   standard — no badges. Name clamp 2, summary clamp 4.
 *   compact  — no badges AND displayName <= 9 chars AND summary <= 90
 *              chars. Name clamp 1, summary clamp 2. The char thresholds
 *              only tune ellipsis frequency; the clamps carry safety.
 *
 * Axes (measured 2026-08-25, scripts/garden-node-pitch-measure.mjs —
 * v2 harness models the REAL `.iconWrap` glow widths 44/54/66 px per
 * status color, the 2-line name clamp, and the reachable status/color
 * pairings; the pre-v2 harness modeled the inner glyph widths and an
 * unclamped name, which understated the green/Answered worst case):
 *   - LOCALE: es/pt strings are longer; the en lane includes the 3rd
 *     (verse) action button, which adds 0 px (never wraps the row).
 *   - WIDTH: five measured node-box columns — w322 (428-430-pt iPhones),
 *     w282 (390-393-pt), w267 (375-pt), w252 (360 CSS px Android), w212
 *     (320 CSS px, the Safari Page Zoom floor). Wrapping is monotone in
 *     width, so each column is conservative for every node box
 *     at-or-above its width; 320 CSS px viewport is the formally
 *     supported minimum.
 *   - ICON GROUP: the `.iconWrap` flex width is data-known from status
 *     (colorFor): narrow = max(blue 44, gold 54), wide = green 66. The
 *     wide wrapper is what forces action-row / meta wraps at some
 *     widths, so green cards get their own column instead of taxing
 *     every blue/gold slot.
 *   - SCALE: near-flat (only rem text grows; chrome is px, rule #15).
 *
 * Slot below = ceil(measured worst class card + 32 breath), pasted from
 * the harness output verbatim. Re-run the script and update this table
 * whenever node card chrome, clamps, or classifier thresholds change.
 */
type TextScale = 'default' | 'large' | 'xl';
type NodeLayoutClass = 'compact' | 'standard' | 'tall';
type WidthBucket = 'w322' | 'w282' | 'w267' | 'w252' | 'w212';
type IconGroup = 'narrow' | 'wide';
type ClassPitch = Record<NodeLayoutClass, Record<TextScale, number>>;
const NODE_CLASS_PITCH: Record<WidthBucket, Record<'en' | 'intl', Record<IconGroup, ClassPitch>>> = {
  w322: {
    en: {
      narrow: {
        tall: { default: 415, large: 434, xl: 453 },
        standard: { default: 340, large: 359, xl: 378 },
        compact: { default: 264, large: 274, xl: 283 },
      },
      wide: {
        tall: { default: 415, large: 434, xl: 453 },
        standard: { default: 340, large: 359, xl: 378 },
        compact: { default: 264, large: 274, xl: 283 },
      },
    },
    intl: {
      narrow: {
        tall: { default: 441, large: 460, xl: 479 },
        standard: { default: 366, large: 385, xl: 404 },
        compact: { default: 291, large: 300, xl: 310 },
      },
      wide: {
        tall: { default: 415, large: 434, xl: 453 },
        standard: { default: 340, large: 359, xl: 378 },
        compact: { default: 264, large: 274, xl: 283 },
      },
    },
  },
  w282: {
    en: {
      narrow: {
        tall: { default: 441, large: 460, xl: 479 },
        standard: { default: 366, large: 385, xl: 404 },
        compact: { default: 291, large: 300, xl: 310 },
      },
      wide: {
        tall: { default: 441, large: 460, xl: 479 },
        standard: { default: 366, large: 385, xl: 404 },
        compact: { default: 291, large: 300, xl: 310 },
      },
    },
    intl: {
      narrow: {
        tall: { default: 460, large: 479, xl: 498 },
        standard: { default: 385, large: 404, xl: 423 },
        compact: { default: 309, large: 318, xl: 328 },
      },
      wide: {
        tall: { default: 441, large: 460, xl: 479 },
        standard: { default: 366, large: 385, xl: 404 },
        compact: { default: 291, large: 300, xl: 310 },
      },
    },
  },
  w267: {
    en: {
      narrow: {
        tall: { default: 441, large: 460, xl: 479 },
        standard: { default: 366, large: 385, xl: 404 },
        compact: { default: 291, large: 300, xl: 310 },
      },
      wide: {
        tall: { default: 441, large: 460, xl: 479 },
        standard: { default: 366, large: 385, xl: 404 },
        compact: { default: 291, large: 300, xl: 310 },
      },
    },
    intl: {
      narrow: {
        tall: { default: 460, large: 479, xl: 498 },
        standard: { default: 385, large: 404, xl: 423 },
        compact: { default: 309, large: 318, xl: 328 },
      },
      wide: {
        tall: { default: 460, large: 479, xl: 498 },
        standard: { default: 385, large: 404, xl: 423 },
        compact: { default: 309, large: 318, xl: 328 },
      },
    },
  },
  w252: {
    en: {
      narrow: {
        tall: { default: 514, large: 533, xl: 552 },
        standard: { default: 439, large: 458, xl: 477 },
        compact: { default: 363, large: 372, xl: 382 },
      },
      wide: {
        tall: { default: 514, large: 533, xl: 552 },
        standard: { default: 439, large: 458, xl: 477 },
        compact: { default: 363, large: 372, xl: 382 },
      },
    },
    intl: {
      narrow: {
        tall: { default: 460, large: 479, xl: 498 },
        standard: { default: 385, large: 404, xl: 423 },
        compact: { default: 309, large: 318, xl: 328 },
      },
      wide: {
        tall: { default: 478, large: 497, xl: 516 },
        standard: { default: 403, large: 422, xl: 441 },
        compact: { default: 327, large: 336, xl: 346 },
      },
    },
  },
  w212: {
    en: {
      narrow: {
        tall: { default: 532, large: 551, xl: 570 },
        standard: { default: 457, large: 476, xl: 495 },
        compact: { default: 381, large: 390, xl: 400 },
      },
      wide: {
        tall: { default: 550, large: 569, xl: 588 },
        standard: { default: 475, large: 494, xl: 513 },
        compact: { default: 399, large: 409, xl: 418 },
      },
    },
    intl: {
      narrow: {
        tall: { default: 573, large: 592, xl: 611 },
        standard: { default: 475, large: 494, xl: 513 },
        compact: { default: 399, large: 409, xl: 418 },
      },
      wide: {
        tall: { default: 573, large: 592, xl: 611 },
        standard: { default: 475, large: 494, xl: 513 },
        compact: { default: 399, large: 409, xl: 418 },
      },
    },
  },
};

/** Compact-class classifier ceilings (display-string chars). Conservative
 * by design — a longer name/summary just lands in `standard`; and if a
 * within-threshold string still wraps past the class allowance at an
 * extreme width, the CSS clamp ellipsizes rather than overlaps. */
const COMPACT_NAME_MAX = 9;
const COMPACT_SUMMARY_MAX = 90;

/** Node content-box width -> measured column. The node box is the wrap
 * width minus the 72 px vine rail and 12 px right inset. Each column is
 * conservative down to the next column's width (monotone wrapping). */
function widthBucketFor(nodeWidth: number): WidthBucket {
  if (nodeWidth >= 322) return 'w322';
  if (nodeWidth >= 282) return 'w282';
  if (nodeWidth >= 267) return 'w267';
  if (nodeWidth >= 252) return 'w252';
  return 'w212';
}
const NODE_START = 60;
const WARM_DAYS = 7;

/**
 * Vertical buffer (in px) above and below the viewport where nodes and
 * vine SVG segments are kept rendered. Sized in pixels (~1 viewport)
 * rather than node count so that text-scale changes (which alter
 * NODE_GAP) don't blow past iOS Safari's max texture size on the SVG
 * layer. At ~800px overscan top + bottom + ~800px viewport, the SVG
 * window is ~2400px tall — well under the 4096px cap on every iPhone
 * generation.
 */
const VINE_OVERSCAN_PX = 800;

/**
 * People list virtualization constants. Each person card is a flex
 * layout with a 1-line-clamped name + meta count line + 28 px vertical
 * padding. We use fixed-pitch absolute positioning (Garden pattern)
 * because names are 1-line-clamped — see `.personCardName` in
 * Garden.module.css — so card height is bounded at every text scale.
 */
const PERSON_ROW_PITCH_BASE = 82;
const PEOPLE_OVERSCAN_PX = 600;
function personRowPitchForScale(scale: 'default' | 'large' | 'xl'): number {
  switch (scale) {
    case 'xl':    return Math.round(PERSON_ROW_PITCH_BASE * 1.18);  // 97
    case 'large': return Math.round(PERSON_ROW_PITCH_BASE * 1.09);  // 89
    default:      return PERSON_ROW_PITCH_BASE;                     // 82
  }
}

/**
 * Hard cap for variable-height list surfaces (PersonView timeline,
 * EvidenceBook testimonies). When a filtered set exceeds this, the
 * surface renders the most recent `LIST_HARD_CAP` items by default
 * and exposes a single inline "Show all N earlier" drilldown that
 * re-renders the surface without the cap. See CLAUDE.md rule #16
 * amendment.
 */
const LIST_HARD_CAP = 50;

/**
 * Class-, scale-, locale-, width-, and icon-group-aware slot lookup.
 * Reads the measured NODE_CLASS_PITCH table directly (see its
 * docstring) — a lookup, never a multiplier.
 */
function nodeSlotFor(
  cls: NodeLayoutClass,
  scale: TextScale,
  isEnglish: boolean,
  bucket: WidthBucket,
  group: IconGroup,
): number {
  const lane = NODE_CLASS_PITCH[bucket][isEnglish ? 'en' : 'intl'];
  return lane[group][cls][scale];
}

function matchesFilter(p: Prayer, filter: Filter): boolean {
  switch (filter) {
    case 'moving': return colorFor(p) === 'gold';
    case 'answered': return statusOf(p) === 'Answered';
    default: return true;
  }
}

export default function Garden({
  prayers,
  onClose,
  onPrayerTap,
  onOpenSummary,
  onOpenPerson,
  onShareCard,
  zIndex,
}: GardenProps) {
  const { t, i18n } = useTranslation();
  // Mid-session recovery for the rule #39 bug-2 scroll freeze (iOS flex-min-size
  // break re-occurring on a reconnect/foreground/re-layout). Measurement lives in
  // the child (VineContent / PeopleGrid), so keying .canvas remounts it cleanly.
  const gardenRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const canvasSlotRef = useRef<HTMLDivElement>(null);
  const [shortBodyHeight, setShortBodyHeight] = useState<number | null>(null);
  // Reading positions (P4): kept here, outside `.canvas`, so Main <-> People
  // and a recovery remount restore them. Recovery holds the last healthy
  // record (the broken container is never read) until the remounted list
  // restores from it.
  const positionRef = useRef<GardenPositionRecord>({ vine: null, people: null, held: false });
  const holdPositionForRecovery = useCallback(() => {
    positionRef.current.held = true;
  }, []);
  // In the short layout the canvas legitimately moves with the outer scroll.
  // Its independently sized slot remains the recovery bound, not its content.
  const canvasRecoverKey = useScrollFreezeRecovery(
    canvasRef, shortBodyHeight === null ? gardenRef : canvasSlotRef, holdPositionForRecovery,
  );
  // App notice stack: the in-flow spacer below the header keeps the search
  // row, the filter row and `.canvas` below the fixed notices (the stack
  // paints above this overlay). Chrome geometry only; `.canvas` is unchanged.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();
  // CR legacy-grouping notice (PC-B rev 9 decision 6): hydration-gated,
  // effect-based, owner-neutral. Reads the RAW quarantined string (never
  // the active map) and recomputes when the hydrated corpus generation or
  // raw merge string changes. `defer` and `hide` render nothing.
  const whoMergeMapRaw = useSettingsStore((s) => s.whoMergeMapRaw);
  const hydration = useAdiaStore((s) => s.hydration);
  const groupingNotice = useMemo(
    () => legacyGroupingNotice(whoMergeMapRaw, hydration, prayers),
    [whoMergeMapRaw, hydration, prayers],
  );
  const [vineFilter, setVineFilter] = useState<Filter>('all');
  const [entryFilter, setEntryFilter] = useState<'all' | 'praise' | 'prayer'>('all');
  const [entryViewRevision, setEntryViewRevision] = useState(0);
  const vinePrayers = useMemo(() => prayers.filter(p => entryFilter === 'all' || isPraise(p) === (entryFilter === 'praise')), [prayers, entryFilter]);
  const [peopleFilter, setPeopleFilter] = useState<PeopleFilter>('all');
  const [layout, setLayout] = useState<Layout>('vine');

  useLayoutEffect(() => {
    const garden = gardenRef.current;
    const header = headerRef.current;
    const controls = controlsRef.current;
    if (!garden || !header || !controls) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const css = getComputedStyle(garden);
      const gardenBox = garden.getBoundingClientRect();
      // Layout viewport only (v0.2.9 NA17 / NA18): the Garden never resizes
      // under the keyboard, so the focused search box and the list stay put.
      const visibleBottom = gardenBox.bottom - (parseFloat(css.paddingBottom) || 0);
      const bodyTop = gardenBox.top + (parseFloat(css.paddingTop) || 0)
        + header.getBoundingClientRect().height + noticeReserve;
      const available = Math.max(0, Math.floor(visibleBottom - bodyTop));
      // Uncapped row heights plus the same 44px control floor in BOTH modes
      // keep activation independent of fallback-only target enlargement.
      // Neither outer viewport nor canvas/content height is an input.
      const controlsHeight = Array.from(controls.children).reduce((total, row) => {
        const style = getComputedStyle(row);
        const targetGrowth = Array.from(row.querySelectorAll('button, input')).reduce(
          (growth, target) => Math.max(growth, 44 - target.getBoundingClientRect().height), 0,
        );
        return total + row.getBoundingClientRect().height + targetGrowth
          + (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      }, 0);
      // Leave room for a 44px action and its surrounding list context.
      const next = available - controlsHeight < 88 ? available : null;
      setShortBodyHeight(current => current === next ? current : next);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(measure);
    };
    const sizes = new ResizeObserver(schedule);
    sizes.observe(garden);
    sizes.observe(header);
    let observed: Element[] = [];
    const observeRows = () => {
      observed.forEach(row => sizes.unobserve(row));
      observed = Array.from(controls.children);
      observed.forEach(row => sizes.observe(row));
      schedule();
    };
    const rows = new MutationObserver(observeRows);
    rows.observe(controls, { childList: true, subtree: true, characterData: true });
    observeRows();
    window.addEventListener('resize', schedule);
    measure();
    return () => {
      if (frame) cancelAnimationFrame(frame);
      sizes.disconnect();
      rows.disconnect();
      window.removeEventListener('resize', schedule);
    };
  }, [noticeReserve, layout]);
  const [sheetPrayer, setSheetPrayer] = useState<Prayer | null>(null);
  // garden-update layer (A1): registered here; Garden mounts with the sheet
  // closed (sheetPrayer === null) -> StrictMode-safe. z goes to the sheet shell.
  const zGardenUpdate = useNavLayer('garden-update', sheetPrayer !== null, () =>
    setSheetPrayer(null),
  );
  // M2 grid fast-path: a People-grid row -> the shared merge sheet. ONE
  // `garden-merge` layer above `garden`; mounts CLOSED (mergeForWho === null)
  // -> StrictMode-safe (rule #78). Garden hosts its OWN merge undo-toast (shared
  // hook, rule #4) so it survives the sheet's unmount over the grid.
  const [mergeForWho, setMergeForWho] = useState<string | null>(null);
  const zGardenMerge = useNavLayer('garden-merge', mergeForWho !== null, () =>
    setMergeForWho(null),
  );
  const {
    toast: gridMergeToast,
    showMergeToast: showGridMergeToast,
    undo: undoGridMerge,
    scheduleDismiss: scheduleGridMergeDismiss,
    cancelDismiss: cancelGridMergeDismiss,
  } = useMergeUndoToast();

  // ---- Verse action (Chunk 2, rule #80) ----
  // Global eligibility, computed once (not per card). The verse lane is
  // flag-gated + English-only (rule #7 carve-out, mirrors PrayerDetail's
  // read-only line + the share fork). Find additionally needs signed-in +
  // online (the endpoint is signed-in + network-bound); View (saved read)
  // needs neither. online is reactive so the Find button hides/shows live.
  const online = useOnlineStore((s) => s.online);
  const { session } = useAuth();
  const signedIn = session !== null;
  // The index and every published result are owner/generation scoped. A
  // session transition remounts VineContent before another owner's corpus can
  // reuse an index, even if the same query text remains in the input.
  const searchScopeKey = `${session?.user.id ?? 'signed-out'}:${getAuthEpoch()}`;
  // Drives BOTH the verse lane (with the flag) AND the node pitch: card
  // height is set by locale string length, not the verse button (see
  // NODE_PITCH). English cards are ~18 px shorter, so they get the smaller
  // pitch; es/pt keep the taller one. Computed here where i18n lives.
  const isEnglishLocale = (i18n.language ?? 'en').slice(0, 2) === 'en';
  const verseLaneOn = VERSE_SUGGEST_ENABLED && isEnglishLocale;
  const verseFindGloballyOk = signedIn && online;
  // ONE saved-verse sheet at a time (resolved set + which prayer), driving
  // the `garden-scripture` NavStack layer; mounts CLOSED -> StrictMode-safe
  // (rule #78). All resolution happens in the tap handlers below, never per
  // card (rule #39 — no per-card async in the virtualized vine).
  const [scriptureSheet, setScriptureSheet] = useState<{
    prayer: Prayer;
    verses: SuggestedVerse[];
  } | null>(null);
  const zGardenScripture = useNavLayer(
    'garden-scripture',
    scriptureSheet !== null,
    () => setScriptureSheet(null),
  );
  // prayerId of an in-flight resolve/find -> the breathing-dot loader +
  // single-flight guard. verseToastKey -> an i18n key for the honest
  // silence/error/unavailable toast (reuses the held-close toast register).
  const [verseBusyFor, setVerseBusyFor] = useState<string | null>(null);
  const [verseToastKey, setVerseToastKey] = useState<string | null>(null);
  // Guard async setState after unmount (the bundle/find resolve can land
  // after the user leaves the Garden).
  const verseMountedRef = useRef(true);
  useEffect(() => {
    verseMountedRef.current = true;
    return () => {
      verseMountedRef.current = false;
    };
  }, []);
  const verseToastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleVerseToastDismiss = () => {
    if (verseToastTimerRef.current) clearTimeout(verseToastTimerRef.current);
    verseToastTimerRef.current = setTimeout(() => {
      setVerseToastKey(null);
      verseToastTimerRef.current = null;
    }, 5000);
  };
  const cancelVerseToastDismiss = () => {
    if (verseToastTimerRef.current) {
      clearTimeout(verseToastTimerRef.current);
      verseToastTimerRef.current = null;
    }
  };
  useEffect(
    () => () => {
      if (verseToastTimerRef.current) clearTimeout(verseToastTimerRef.current);
    },
    [],
  );
  const showVerseToast = (key: string) => {
    setVerseToastKey(key);
    scheduleVerseToastDismiss();
  };

  // View: SAVED-ONLY pure read (no API, no write). [] = total bundle drift
  // OR an uncached offline bundle (first View before any online resolve) ->
  // honest "unavailable" toast, never a silent empty sheet (plan §6.3).
  const handleViewScripture = async (prayer: Prayer) => {
    if (verseBusyFor) return;
    setVerseBusyFor(prayer.id);
    try {
      const verses = await resolveSavedVerses(prayer);
      if (!verseMountedRef.current) return;
      if (verses.length > 0) setScriptureSheet({ prayer, verses });
      else showVerseToast('garden.verseUnavailable');
    } catch {
      if (verseMountedRef.current) showVerseToast('garden.verseUnavailable');
    } finally {
      if (verseMountedRef.current) setVerseBusyFor(null);
    }
  };

  // Find: the PAID lane (persists verseResult). SYNCHRONOUS GUARD re-checks
  // at tap time before any wire call — the render-time button gate can go
  // stale, and verse-suggest.ts trusts the caller to never invoke it for a
  // private prayer (rules #27/#76/#80). getOnline() is a fresh snapshot.
  const handleFindScripture = async (prayer: Prayer) => {
    if (
      verseBusyFor ||
      !verseLaneOn ||
      prayer.isPrivate ||
      !prayer.rawText.trim() ||
      isCrisisPrayer(prayer.rawText) ||
      !signedIn ||
      !getOnline()
    ) {
      return;
    }
    setVerseBusyFor(prayer.id);
    try {
      const verses = await getVersesForPrayer(prayer);
      if (!verseMountedRef.current) return;
      if (verses && verses.length > 0) setScriptureSheet({ prayer, verses });
      else showVerseToast('share.verseFoundSilence');
    } catch (e) {
      if (!verseMountedRef.current) return;
      showVerseToast(
        e instanceof VerseSuggestError && e.code === 'rate-limited'
          ? 'share.verseFoundRateLimit'
          : 'share.verseFoundError',
      );
    } finally {
      if (verseMountedRef.current) setVerseBusyFor(null);
    }
  };
  // Vine-view full-text search. The controlled input is immediate; the index
  // render is deferred so typing can paint first. While React still holds the
  // prior deferred value, VineContent paints a neutral searching state rather
  // than stale results from a different visible query.
  const [vineSearch, setVineSearch] = useState('');
  const deferredVineSearch = useDeferredValue(vineSearch);
  const activeVineSearch = vineSearch.trim() === '' ? vineSearch : deferredVineSearch;
  const searchPending = vineSearch.trim() !== '' && activeVineSearch !== vineSearch;
  const searchInputRef = useRef<HTMLInputElement>(null);

  // A result stamp must match every source of truth before it is announced:
  // query, status filter, immutable corpus generation, and owner/auth epoch.
  const [searchResult, setSearchResult] = useState<{
    count: number;
    query: string;
    filter: Filter;
    fuzzy: boolean;
    related: boolean;
    englishRelated: boolean;
    corpus: Prayer[] | null;
    scopeKey: string;
  }>({ count: 0, query: '', filter: 'all', fuzzy: false, related: false, englishRelated: false, corpus: null, scopeKey: '' });
  const handleResultCount = useCallback(
    (
      count: number,
      query: string,
      filter: Filter,
      fuzzy: boolean,
      related: boolean,
      englishRelated: boolean,
      corpus: Prayer[],
      scopeKey: string,
    ) => {
      setSearchResult({ count, query, filter, fuzzy, related, englishRelated, corpus, scopeKey });
    },
    [],
  );
  const searchResultIsCurrent =
    !searchPending &&
    searchResult.query === vineSearch &&
    searchResult.filter === vineFilter &&
    searchResult.englishRelated === isEnglishLocale &&
    searchResult.corpus === vinePrayers &&
    searchResult.scopeKey === searchScopeKey;
  const clearVineSearch = useCallback(() => {
    // Focus synchronously while the trusted click/keyboard event is active;
    // WebKit may defer or suppress a focus request made only from rAF after
    // the clear button has unmounted. The rAF repeat covers React's commit.
    searchInputRef.current?.focus();
    setVineSearch('');
    requestAnimationFrame(() => searchInputRef.current?.focus());
  }, []);
  // Held-close airplane tap surfaces a transient bottom toast instead of
  // routing through the share flow. Single-slot id = latest-wins reset
  // when the user taps another held-close airplane during the dismiss
  // window. Helpers mirror PrayPage's undoToast pattern.
  const [heldCloseToastFor, setHeldCloseToastFor] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleToastDismiss = () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => {
      setHeldCloseToastFor(null);
      toastTimerRef.current = null;
    }, 5000);
  };
  const cancelToastDismiss = () => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  };
  useEffect(() => () => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
  }, []);

  const handleAirplaneTap = (prayer: Prayer) => {
    if (prayer.isPrivate) {
      setHeldCloseToastFor(prayer.id);
      scheduleToastDismiss();
    } else {
      onShareCard?.(prayer);
    }
  };

  const totalMoving = useMemo(
    () => prayers.filter((p) => !isPraise(p) && colorFor(p) === 'gold').length,
    [prayers],
  );

  const vineFilterOptions: { key: Filter; label: string }[] = [
    { key: 'all', label: t('garden.filterAll') },
    { key: 'moving', label: totalMoving ? t('garden.segmentMovingCount', { count: totalMoving }) : t('garden.segmentMoving') },
    { key: 'answered', label: t('garden.filterAnswered') },
  ];

  const peopleFilterOptions: { key: PeopleFilter; label: string }[] = [
    { key: 'all', label: t('people.filterAll') },
    { key: 'month', label: t('people.filterMonth') },
    { key: '90d', label: t('people.filter90d') },
    { key: 'answered', label: t('people.filterAnswered') },
  ];

  return (
    <div
      ref={gardenRef}
      className={styles.garden}
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={t('garden.title')}
      data-garden-surface
    >
      <header ref={headerRef} className={styles.header}>
        <button type="button" className={styles.close} onClick={onClose} aria-label={t('nav.back')}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="14 6 8 12 14 18" />
          </svg>
        </button>

        {prayers.length > 0 && (
          <nav className={styles.headerActions} aria-label={t('people.layoutGridAria')}>
            <button
              type="button"
              role="tab"
              aria-selected={layout === 'vine'}
              className={`${styles.navBtn} ${layout === 'vine' ? styles.navBtnActive : ''}`}
              onClick={() => setLayout('vine')}
            >
              {t('people.layoutVineLabel')}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={layout === 'grid'}
              className={`${styles.navBtn} ${layout === 'grid' ? styles.navBtnActive : ''}`}
              onClick={() => { setLayout('grid'); setPeopleFilter('all'); }}
            >
              {t('people.layoutGridLabel')}
            </button>
            <button
              type="button"
              className={styles.rememberBtn}
              onClick={onOpenSummary}
            >
              {t('faithfulness.remember')}
            </button>
          </nav>
        )}
      </header>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      <div
        className={`${styles.body} ${shortBodyHeight !== null ? styles.shortBody : ''}`}
        style={shortBodyHeight !== null ? { '--garden-body-height': `${shortBodyHeight}px` } as CSSProperties : undefined}
        data-garden-body
        data-garden-short-height={shortBodyHeight !== null ? 'true' : undefined}
      >
      <div ref={controlsRef} className={styles.bodyControls} data-garden-controls>
      {groupingNotice === 'show' && (
        <p className={styles.crGroupingNotice} role="status">
          {t('people.legacyGroupingNotice')}
        </p>
      )}

      {prayers.length > 0 && layout === 'vine' && (
        <div className={styles.filtersRow} role="tablist" aria-label={t('entry.types')}>
          {(['all', 'praise', 'prayer'] as const).map(kind => (
            <button key={kind} type="button" role="tab" aria-selected={entryFilter === kind}
              className={`${styles.filterBtn} ${entryFilter === kind ? styles.filterBtnActive : ''}`}
              onClick={() => {
                if (kind !== entryFilter) setEntryViewRevision(revision => revision + 1);
                setEntryFilter(kind); setVineFilter('all');
              }}>
              {t(kind === 'all' ? 'garden.filterAll' : `entry.${kind}Label`)}
            </button>
          ))}
        </div>
      )}

      {prayers.length > 0 && layout === 'vine' && (
        <div
          className={styles.vineSearchRow}
          role="search"
          aria-label={t('garden.searchLabel')}
        >
          <label className={styles.srOnly} htmlFor="garden-prayer-search">
            {t('garden.searchLabel')}
          </label>
          <div className={styles.searchWrap}>
            <svg
              className={styles.searchIcon}
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              id="garden-prayer-search"
              ref={searchInputRef}
              type="search"
              className={styles.searchInput}
              value={vineSearch}
              onChange={(e) => setVineSearch(boundPrayerSearchInput(e.target.value))}
              onKeyDown={(event) => {
                if (event.key !== 'Escape' || vineSearch === '') return;
                event.preventDefault();
                event.stopPropagation();
                clearVineSearch();
              }}
              placeholder={t('garden.searchPlaceholder')}
              aria-describedby={vineSearch.trim() !== '' ? 'garden-search-status' : undefined}
              aria-busy={searchPending}
              maxLength={PRAYER_SEARCH_MAX_QUERY_LENGTH}
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
            />
            {vineSearch && (
              <button
                type="button"
                className={styles.searchClear}
                onClick={clearVineSearch}
                aria-label={t('garden.searchClear')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            )}
          </div>
          {vineSearch.trim() !== '' && (
            <div
              id="garden-search-status"
              className={styles.searchStatus}
              role="status"
              aria-live="polite"
              aria-atomic="true"
              data-search-status={searchPending || !searchResultIsCurrent ? 'pending' : 'current'}
            >
              {searchPending || !searchResultIsCurrent ? (
                <p className={styles.searchCount}>{t('garden.searchSearching')}</p>
              ) : (
                <>
                  {searchResult.fuzzy && (
                    <p className={styles.searchFuzzyNote}>
                      {t('garden.searchFuzzyNote')}
                    </p>
                  )}
                  {searchResult.related && (
                    <p className={styles.searchFuzzyNote}>
                      {t('garden.searchRelatedNote')}
                    </p>
                  )}
                  <p className={styles.searchCount}>
                    {t('garden.searchCount', { count: searchResult.count })}
                  </p>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {prayers.length > 0 && layout === 'vine' && (
        <div className={styles.filtersRow} role="tablist" aria-label={t('garden.title')}>
          {vineFilterOptions.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={vineFilter === f.key}
              className={`${styles.filterBtn} ${vineFilter === f.key ? styles.filterBtnActive : ''}`}
              onClick={() => { setVineFilter(f.key); if (f.key !== 'all') setEntryFilter('prayer'); }}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {prayers.length > 0 && layout === 'grid' && (
        <div className={styles.filtersRow} role="tablist" aria-label={t('people.layoutGridAria')}>
          {peopleFilterOptions.map((f) => (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={peopleFilter === f.key}
              className={`${styles.filterBtn} ${peopleFilter === f.key ? styles.filterBtnActive : ''}`}
              onClick={() => setPeopleFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      </div>
      <div
        ref={canvasSlotRef}
        className={styles.canvasSlot}
        data-garden-canvas-slot
      >
      <div
        ref={canvasRef}
        key={canvasRecoverKey}
        className={`${styles.canvas} ${styles.bodyCanvas}`}
        data-garden-scroll
      >
        {layout === 'grid' ? (
          <PeopleGrid
            key={searchScopeKey}
            prayers={prayers}
            filter={peopleFilter}
            searchScopeKey={searchScopeKey}
            position={positionRef}
            onOpenPerson={onOpenPerson}
            onOpenMerge={setMergeForWho}
            t={t}
          />
        ) : (
          <VineContent
            key={`${searchScopeKey}:${entryViewRevision}`}
            entryFilter={entryFilter}
            prayers={vinePrayers}
            filter={vineFilter}
            searchQuery={activeVineSearch}
            searchPending={searchPending}
            searchScopeKey={searchScopeKey}
            onResultCount={handleResultCount}
            onPrayerTap={onPrayerTap}
            onReply={setSheetPrayer}
            onAirplaneTap={handleAirplaneTap}
            isEnglishLocale={isEnglishLocale}
            language={i18n.language ?? 'en'}
            verseLaneOn={verseLaneOn}
            verseFindGloballyOk={verseFindGloballyOk}
            verseBusyId={verseBusyFor}
            onViewScripture={handleViewScripture}
            onFindScripture={handleFindScripture}
            position={positionRef}
            t={t}
          />
        )}
      </div>

      </div>
      </div>

      {/* Cutover: never open the composer, even from a stale state. */}
      {sheetPrayer && !isCoreReadOnly() && (
        <GardenUpdateSheet
          prayer={sheetPrayer}
          onClose={() => setSheetPrayer(null)}
          zIndex={zGardenUpdate}
        />
      )}

      {!CR_CONTAINMENT_ACTIVE && mergeForWho !== null && (
        <MergePersonSheet
          subjectWho={mergeForWho}
          prayers={prayers}
          onClose={() => setMergeForWho(null)}
          onMerged={({ prevMap, keptName, absorbedName }) => {
            showGridMergeToast(absorbedName, keptName, prevMap);
            setMergeForWho(null);
          }}
          zIndex={zGardenMerge}
        />
      )}

      {gridMergeToast && (
        <MergeUndoToast
          toast={gridMergeToast}
          onUndo={undoGridMerge}
          onPauseDismiss={cancelGridMergeDismiss}
          onResumeDismiss={scheduleGridMergeDismiss}
        />
      )}

      {heldCloseToastFor && (
        <div
          className={styles.heldCloseToast}
          role="status"
          aria-live="assertive"
          onMouseEnter={cancelToastDismiss}
          onMouseLeave={scheduleToastDismiss}
          onFocus={cancelToastDismiss}
          onBlur={scheduleToastDismiss}
        >
          {t('private.heldCloseShareToast')}
        </div>
      )}

      {/* Read-only saved-verse sheet, opened by a vine card's verse
          action (View or a successful Find). Garden resolves the verses
          in the tap handler and passes them in; the sheet itself never
          searches. `garden-scripture` NavStack layer + neutral close
          aria (closing returns to the Garden, not a prayer). */}
      {scriptureSheet && (
        <ScriptureVersesSheet
          zIndex={zGardenScripture}
          verses={scriptureSheet.verses}
          onClose={() => setScriptureSheet(null)}
          closeAriaKey="nav.back"
        />
      )}

      {/* Verse busy indicator — breathing dot in the GatheringIndicator
          register (not a card spinner), while a resolve/find is in
          flight. */}
      {verseBusyFor && (
        <div className={styles.verseBusy} role="status" aria-live="polite">
          <span className={styles.verseBusyDot} aria-hidden="true" />
          {t('garden.verseLoading')}
        </div>
      )}

      {/* Honest silence/error/unavailable toast for the verse action.
          Reuses the held-close toast register + pause-on-hover. */}
      {verseToastKey && (
        <div
          className={styles.heldCloseToast}
          role="status"
          aria-live="assertive"
          onMouseEnter={cancelVerseToastDismiss}
          onMouseLeave={scheduleVerseToastDismiss}
          onFocus={cancelVerseToastDismiss}
          onBlur={scheduleVerseToastDismiss}
        >
          {t(verseToastKey)}
        </div>
      )}
    </div>
  );
}

/* ---- Vine content (windowed virtualization) ----
 *
 * The Garden vine renders only the prayers visible in the viewport
 * (plus a ~1-viewport overscan above and below). Total scroll height
 * stays accurate via `min-height: max(curveEndY + 100, lastNodeY + NODE_GAP)`
 * on the wrapper (a full measured slot reserved below the last real prayer so
 * its card is fully scrollable; see buildVineGeometry), but the SVG layer is
 * sized to cover only the
 * viewport window — never the full vine. This eliminates iOS Safari's
 * "render of 24,000-px-tall GPU layer" failure mode at any scale.
 *
 * Layout retains one numeric top per prayer. Binary selectors find the
 * visible item/coordinate ranges, then centerline points, knuckles, and
 * tendrils are derived only for that window — O(log n + window), with no
 * full-vine geometry-object cache. The seeded-random formulas remain the
 * same, so visible vine character is preserved at any corpus size.
 *
 * See CLAUDE.md § "Garden virtualization" for the architecture
 * guardrails (do NOT add `will-change: transform` to the wrapper, do
 * NOT add `filter` keyframes to off-screen-eligible animations, etc.).
 */

interface VineContentProps {
  entryFilter: 'all' | 'praise' | 'prayer';
  prayers: Prayer[];
  filter: Filter;
  searchQuery: string;
  searchPending: boolean;
  searchScopeKey: string;
  onResultCount?: (
    n: number,
    query: string,
    filter: Filter,
    fuzzy: boolean,
    related: boolean,
    englishRelated: boolean,
    corpus: Prayer[],
    scopeKey: string,
  ) => void;
  onPrayerTap: (id: string) => void;
  onReply: (prayer: Prayer) => void;
  onAirplaneTap: (prayer: Prayer) => void;
  /* Drives the node pitch (NODE_PITCH): English cards are ~18 px shorter
     than es/pt, so they get the smaller pitch. Locale (not the verse
     button) is the height axis — see NODE_PITCH docstring. */
  isEnglishLocale: boolean;
  /* Full i18n language tag (e.g. 'es-MX') for the relative-time bucket
     labels' Intl weekday/month formatting (rule #6). */
  language: string;
  /* Verse action (Chunk 2, rule #80). `verseLaneOn` = flag + English
     (global). `verseFindGloballyOk` = signed-in + online (global; the
     Find path is network-bound). `verseBusyId` = the prayer id with an
     in-flight resolve/find so its button can disable. Handlers do the
     async resolve in the parent — NEVER per card (rule #39). */
  verseLaneOn: boolean;
  verseFindGloballyOk: boolean;
  /** Garden's reading-position record (P4). */
  position: { current: GardenPositionRecord };
  verseBusyId: string | null;
  onViewScripture: (prayer: Prayer) => void;
  onFindScripture: (prayer: Prayer) => void;
  t: TFn;
}

interface VineGeometry {
  vineHeight: number;
  count: number;
  startY: number;
  endY: number;
  nodeTops: number[];
}

interface VisibleVineGeometry {
  centerlinePoints: Array<{ x: number; y: number }>;
  knuckles: Array<{ x: number; y: number; rx: number; ry: number; angle: number }>;
  tendrils: Array<{ d: string; midY: number }>;
}

// getVineX (parametric centerline) + vineRand (seeded per-index variance) live
// in @/lib/vine-math (extracted under rule #4). Bodies are byte-equivalent; the
// vine drifts identically.

// Builds the vine geometry from the ACTUAL node layout tops (layoutItems
// output filtered to node items), NOT a uniform `NODE_START + j * NODE_GAP`
// stride — bucket markers (sticky time anchors) insert vertical gaps between
// nodes, so a node's Y shifts. Knuckle SIZE/ANGLE stays seeded by node index
// `j` (vineRand) so each prayer's swelling is stable, but its X/Y move with the
// node (the vine re-wanders when markers are present — intended, not a
// regression). `vineHeight` here is only the geometry's own curve-bottom +
// single-prayer-sprig reservation; the caller takes max(total, vineHeight) for
// the actual scroll min-height so the last card always has a full slot below it
// (the a3415d6 tail-reservation fix carried into the layoutItems model).
function buildVineGeometry(nodeTops: number[], maxSlot: number): VineGeometry {
  const count = nodeTops.length;
  if (count === 0) {
    return { vineHeight: 0, count: 0, startY: 0, endY: 0, nodeTops };
  }
  const lastNodeTop = nodeTops[count - 1];
  // Sprig: a SINGLE-prayer vine drifts one full extra slot (maxSlot, the
  // tall-class slot) past the only node — the young-sprig tail. For
  // count >= 2 the curve ends 20 px past the last knuckle, full stop.
  // The old `max(lastNodeTop, NODE_START + (count - 1) * NODE_GAP)` form
  // was only ever equivalent to lastNodeTop under the UNIFORM pitch;
  // with per-class slots (density arc 2026-08-25) it overshot by the
  // summed compact savings, reserving thousands of px of blank scroll
  // past the oldest prayer on a large garden. The scroll bottom is now
  // `total` (each card's own slot, including the last) via the caller's
  // max(total, vineHeight) — the vine tip ending just past the last
  // knuckle IS the end-of-garden cue.
  const curveEndY = (count === 1 ? lastNodeTop + maxSlot : lastNodeTop) + 20;
  const startY = NODE_START - 20;
  const endY = curveEndY;
  // +80 over curveEndY reproduces the prior single-prayer reservation shape
  // (count 1: curveEndY = NODE_START + maxSlot + 20 -> vineHeight =
  // NODE_START + maxSlot + 100). The caller's max(total, vineHeight) makes
  // the multi-prayer reservation = total (each card's own full slot,
  // including the last).
  const vineHeight = curveEndY + 80;

  // Retain only the O(n) numeric node tops. The former implementation eagerly
  // retained 4n centerline objects plus n knuckles and n path strings. Visible
  // decoration is now derived below in O(log n + window) on each scroll.
  return { vineHeight, count, startY, endY, nodeTops };
}

function buildVisibleVineGeometry(
  geometry: VineGeometry,
  minimumY: number,
  maximumY: number,
): VisibleVineGeometry {
  const { count, startY, endY, nodeTops } = geometry;
  if (count === 0 || endY <= startY) {
    return { centerlinePoints: [], knuckles: [], tendrils: [] };
  }

  const steps = Math.max(12, count * 4);
  const firstStep = Math.max(
    0,
    Math.ceil(((minimumY - startY) / (endY - startY)) * steps),
  );
  const lastStep = Math.min(
    steps,
    Math.floor(((maximumY - startY) / (endY - startY)) * steps),
  );
  const centerlinePoints: VisibleVineGeometry['centerlinePoints'] = [];
  for (let step = firstStep; step <= lastStep; step += 1) {
    const ratio = step / steps;
    centerlinePoints.push({
      x: getVineX(ratio),
      y: startY + (endY - startY) * ratio,
    });
  }

  const nodeRange = gardenCoordinateRange(
    nodeTops,
    (top) => top,
    minimumY,
    maximumY,
  );
  const knuckles: VisibleVineGeometry['knuckles'] = [];
  for (let index = nodeRange.start; index < nodeRange.end; index += 1) {
    const nodeY = nodeTops[index];
    const ratio = (nodeY - startY) / (endY - startY);
    knuckles.push({
      x: getVineX(ratio),
      y: nodeY,
      rx: 6 + vineRand(index, 1) * 2.2,
      ry: 4.2 + vineRand(index, 2) * 1.6,
      angle: (vineRand(index, 3) - 0.5) * 36,
    });
  }

  const tendrils: VisibleVineGeometry['tendrils'] = [];
  const firstTendril = Math.max(0, nodeRange.start - 1);
  const lastTendril = Math.min(count - 2, nodeRange.end);
  for (let index = firstTendril; index <= lastTendril; index += 1) {
    const bias = 0.35 + vineRand(index, 5) * 0.3;
    const midY = nodeTops[index] + (nodeTops[index + 1] - nodeTops[index]) * bias;
    if (midY < minimumY || midY > maximumY) continue;
    const ratio = (midY - startY) / (endY - startY);
    const baseX = getVineX(ratio);
    const side = index % 2 === 0 ? 1 : -1;
    const len = 7 + vineRand(index, 7) * 4;
    const curl = 4 + vineRand(index, 9) * 3;
    const d =
      `M ${baseX} ${midY} ` +
      `c ${len * 0.5 * side} ${-1.5}, ${len * side} ${-3}, ${len * 0.75 * side} ${-curl} ` +
      `c ${-len * 0.35 * side} ${-1.5}, ${-len * 0.1 * side} ${1.5}, ${len * 0.25 * side} ${curl * 0.55}`;
    tendrils.push({ d, midY });
  }

  return { centerlinePoints, knuckles, tendrils };
}

// ---- Virtual item stream layout (mirrors RememberStream's layoutItems) ----
//
// Variable per-item pitch: nodes use their layout CLASS's measured slot
// (density plan 2026-08-25), bucket markers use BUCKET_PITCH. `item.kind`
// stays binary (node | bucket) — the class rides on the LAYOUT as
// `layoutClass`, so every `kind === 'node'` branch (nodeTops, render,
// sticky-bucket walk, stagger) is untouched. Garden deviation from
// RememberStream: `y` SEEDS at NODE_START (not 0) so the first node's
// position matches the pre-density layout.
const BUCKET_PITCH = 52;

interface VineItemLayout {
  item: VineItem;
  top: number;
  height: number;
  /** Present on node items only — drives the slot height AND the
   * clamp-enforcing CSS modifier on the rendered card. */
  layoutClass?: NodeLayoutClass;
}

function layoutItems(
  items: VineItem[],
  classify: (prayer: Prayer) => NodeLayoutClass,
  slotOf: (cls: NodeLayoutClass, prayer: Prayer) => number,
): { layouts: VineItemLayout[]; total: number } {
  const layouts: VineItemLayout[] = [];
  let y = NODE_START;
  for (const item of items) {
    if (item.kind === 'bucket') {
      layouts.push({ item, top: y, height: BUCKET_PITCH });
      y += BUCKET_PITCH;
    } else {
      const layoutClass = classify(item.prayer);
      const h = slotOf(layoutClass, item.prayer);
      layouts.push({ item, top: y, height: h, layoutClass });
      y += h;
    }
  }
  return { layouts, total: y };
}

// Reading position (garden-remember P4, 2026-10-17). Owned by Garden, outside
// the shared `.canvas` scroll container, so it survives a Main <-> People
// switch (which unmounts the list) and the freeze-recovery remount (which
// replaces `.canvas`). Each entry is tagged with the exact account/auth scope,
// query and filter it was taken under; a mismatched tag never restores.
interface GardenPositionRecord {
  /** Topmost visible vine item + offset into it, and the raw scrollTop fallback. */
  vine: { tag: string; key: string; offset: number; rawTop: number } | null;
  /** People list raw scrollTop (fixed-pitch rows; nothing to re-anchor). */
  people: { tag: string; scrollTop: number } | null;
  /** Set just before a freeze-recovery remount; the next restore clears it. */
  held: boolean;
}

const vineItemKey = (item: VineItem) =>
  item.kind === 'bucket' ? `b:${item.bucket.id}` : `n:${item.prayer.id}`;

/** Only a genuinely scrollable container records, so a frozen one never does. */
function scrollsNow(el: HTMLElement): boolean {
  return el.scrollHeight - el.clientHeight >= 4;
}

/**
 * How far the vine is scrolled, in vine (layout) coordinates. The sticky
 * bucket label is in flow above `.vineWrap`, so the container's scrollTop
 * includes its height whenever it shows (and the engines' own scroll anchoring
 * shifts scrollTop by that height as it appears). Container rects only, the
 * same class as VirtualPersonList's list offset; never card DOM (rule #39).
 */
function vineScrolled(el: HTMLElement, wrap: HTMLElement): number {
  return el.getBoundingClientRect().top - wrap.getBoundingClientRect().top;
}

/** Binary lookup over the computed layouts; never reads card DOM (rule #39). */
function recordVinePosition(
  record: GardenPositionRecord,
  el: HTMLElement,
  wrap: HTMLElement,
  layouts: VineItemLayout[],
  tag: string,
) {
  if (record.held || layouts.length === 0 || !scrollsNow(el)) return;
  const local = vineScrolled(el, wrap);
  const at = gardenLastAtOrBefore(layouts, local) ?? layouts[0];
  record.vine = { tag, key: vineItemKey(at.item), offset: local - at.top, rawTop: el.scrollTop };
}

// Maps a bucket to its display label. Static i18n keys for the relative
// buckets; `Intl` for the per-day weekday and per-month name; the numeral for
// year. Locale-tag convention en-US / es-MX / pt-BR (rule #6).
function bucketLabelFor(bucket: Bucket, t: TFn, language: string): string {
  const localeTag = language.startsWith('es')
    ? 'es-MX'
    : language.startsWith('pt')
    ? 'pt-BR'
    : 'en-US';
  const d = new Date(bucket.anchorISO);
  switch (bucket.kind) {
    case 'today':
      return t('garden.bucketToday');
    case 'yesterday':
      return t('garden.bucketYesterday');
    case 'weekday':
      return new Intl.DateTimeFormat(localeTag, { weekday: 'long' }).format(d);
    case 'lastWeek':
      return t('garden.bucketLastWeek');
    case 'earlierThisMonth':
      return t('garden.bucketEarlierThisMonth');
    case 'month':
      return new Intl.DateTimeFormat(localeTag, { month: 'long' }).format(d);
    case 'year':
      return String(d.getFullYear());
  }
}

// Stitch a cubic-bezier path from centerline points, with `yOffset`
// subtracted so the path renders relative to the SVG window's origin.
// Same control-point ratios (0.35 / 0.65) as the original full-vine
// stitcher — visually byte-identical for any windowed slice.
function stitchVinePath(points: Array<{ x: number; y: number }>, yOffset: number): string {
  if (points.length === 0) return '';
  let prevX = points[0].x;
  let prevY = points[0].y - yOffset;
  let d = `M ${prevX} ${prevY}`;
  for (let i = 1; i < points.length; i++) {
    const x = points[i].x;
    const y = points[i].y - yOffset;
    const cp1y = prevY + (y - prevY) * 0.35;
    const cp2y = prevY + (y - prevY) * 0.65;
    d += ` C ${prevX} ${cp1y}, ${x} ${cp2y}, ${x} ${y}`;
    prevX = x;
    prevY = y;
  }
  return d;
}

function VineContent({
  entryFilter,
  prayers,
  filter,
  searchQuery,
  searchPending: searchPendingInput,
  searchScopeKey,
  onResultCount,
  onPrayerTap,
  onReply,
  onAirplaneTap,
  isEnglishLocale,
  language,
  verseLaneOn,
  verseFindGloballyOk,
  verseBusyId,
  onViewScripture,
  onFindScripture,
  position,
  t,
}: VineContentProps) {
  // Subscribe so the vine reflows if the user toggles text scale in
  // Settings while the Garden sits in the back stack.
  const textScale = useSettingsStore((s) => s.textScale);
  // LC4: pending recordings the queue run cannot process in this page (page
  // memory only); their cards show the review badge, not a finishing spinner.
  const recordingAttention = useAudioQueueStore((s) => s.attention);
  // Live wrap width feeds the width-bucketed pitch column (density plan
  // § narrow-width). Container-width-keyed constants are the same
  // measurement class the windowing already performs (clientHeight /
  // scrollTop) — NOT per-instance card measurement. 0 until the layout
  // effect seeds it; the conservative pre-measure render below doesn't
  // depend on exact slot heights.
  const [wrapWidth, setWrapWidth] = useState(0);
  const widthBucket = widthBucketFor(wrapWidth > 0 ? wrapWidth - 84 : 267);
  // MAX_SLOT: the tallest slot for the active scale/locale/width (max over
  // icon groups). Replaces the old uniform NODE_GAP in its residual roles
  // (geometry slice margin, single-prayer sprig reservation).
  const MAX_SLOT = Math.max(
    nodeSlotFor('tall', textScale, isEnglishLocale, widthBucket, 'narrow'),
    nodeSlotFor('tall', textScale, isEnglishLocale, widthBucket, 'wide'),
  );

  // Relative-time bucket labels ("Today" / "Yesterday" / weekday) are computed
  // against `now`, captured at mount and refreshed on visibility-refocus so a
  // Garden left open across midnight re-labels when the user returns (the one
  // real difference from RememberStream's ABSOLUTE year markers).
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNow(new Date());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Index ownership is explicit and immutable. WeakMap field caching inside
  // the builder reuses normalization only for Prayer objects that lifecycle
  // code has not replaced.
  //
  // P2 (garden-remember readiness, 2026-10-16): no index is built during
  // render. After paint the whole index (normalization included) is built in
  // bounded idle slices and published here; a corpus or scope change cancels
  // the obsolete build. The kept index is reused per exact (array, scope), and
  // stable Prayer objects from loadPrayers keep the field cache across syncs.
  // A query typed before the index for this exact corpus and scope is ready
  // shows the existing searching state.
  const [builtIndex, setBuiltIndex] = useState<{
    prayers: Prayer[];
    scopeKey: string;
    index: PrayerSearchIndex;
  } | null>(null);
  useEffect(
    () => runStepsInSlices(prayerSearchIndexSteps(prayers, searchScopeKey), (index) =>
      setBuiltIndex({ prayers, scopeKey: searchScopeKey, index })),
    [prayers, searchScopeKey],
  );
  const searchIndex = builtIndex?.prayers === prayers && builtIndex.scopeKey === searchScopeKey
    ? builtIndex.index
    : null;
  const searchPending = searchPendingInput || (searchQuery.trim() !== '' && searchIndex === null);

  // Empty-query order stays date-descending, with id as the stable fallback
  // for duplicate timestamps regardless of array arrival order.
  const sorted = useMemo(
    () =>
      [...prayers].sort((a, b) => {
        const byDate = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        return byDate !== 0 ? byDate : a.id.localeCompare(b.id);
      }),
    [prayers],
  );

  const { filtered, isFuzzy, isRelated, matchKindByPrayerId } = useMemo(() => {
    if (searchPending) {
      return {
        filtered: [],
        isFuzzy: false,
        isRelated: false,
        matchKindByPrayerId: new Map<string, PrayerSearchMatchKind>(),
      };
    }
    if (searchQuery.trim()) {
      // Exact/fuzzy choice happens across all statuses, then the selected
      // Garden status composes afterward. Match provenance is retained per
      // prayer so a direct hit outside the active filter cannot suppress the
      // related treatment of a visible row (or mark direct rows as related).
      const relatedTerms = isEnglishLocale
        ? expandQueryToSynonymTerms(searchQuery)
        : [];
      const result = searchPrayerIndex(searchQuery, searchIndex!, undefined, relatedTerms);
      const visible = result.prayers.filter((prayer) => matchesFilter(prayer, filter));
      return {
        filtered: visible,
        isFuzzy: visible.some((prayer) => result.matchKindByPrayerId.get(prayer.id) === 'fuzzy'),
        isRelated: visible.some((prayer) => result.matchKindByPrayerId.get(prayer.id) === 'related'),
        matchKindByPrayerId: result.matchKindByPrayerId,
      };
    }
    return {
      filtered: sorted.filter((prayer) => matchesFilter(prayer, filter)),
      isFuzzy: false,
      isRelated: false,
      matchKindByPrayerId: new Map<string, PrayerSearchMatchKind>(),
    };
  }, [searchPending, searchQuery, searchIndex, sorted, filter, isEnglishLocale]);

  // Sticky time anchors: interleave relative-time bucket markers into the
  // ALREADY-date-desc-sorted list. Suppressed during an active text search —
  // results are relevance-ranked (Plans 03/04), so date buckets would be out
  // of order; the node-only branch keeps the windowing path identical.
  const showBuckets = !searchPending && searchQuery.trim() === '';
  const items = useMemo<VineItem[]>(() => {
    if (!showBuckets) return filtered.map((p) => ({ kind: 'node', prayer: p }));
    return interleaveBucketMarkers(filtered, now);
  }, [filtered, showBuckets, now]);

  // Virtual stream layout (RememberStream parity): per-item {top, height},
  // node = its layout class's measured slot, marker = BUCKET_PITCH. ONE path
  // drives windowing for both bucket and node-only (search) states. The
  // classifier reads DATA only (badges are exact booleans; the char
  // thresholds are safety-backed by the CSS clamps) and uses the SAME
  // display strings the render paints, so class and paint can't drift.
  const { layouts, total } = useMemo(() => {
    const classify = (prayer: Prayer): NodeLayoutClass => {
      if (prayer.isPrivate || prayer.pendingTranscriptionId) return 'tall';
      const rawWho = whoOf(prayer);
      const displayName = isPraise(prayer) ? t('entry.praiseLabel') : isSelf(rawWho) ? t('people.you') : rawWho;
      const summary = prayer.gardenSummary || cardSummary(prayer);
      if (
        displayName.length <= COMPACT_NAME_MAX &&
        summary.length <= COMPACT_SUMMARY_MAX
      ) {
        return 'compact';
      }
      return 'standard';
    };
    const slotOf = (cls: NodeLayoutClass, prayer: Prayer) =>
      nodeSlotFor(
        cls,
        textScale,
        isEnglishLocale,
        widthBucket,
        colorFor(prayer) === 'green' ? 'wide' : 'narrow',
      );
    return layoutItems(items, classify, slotOf);
  }, [items, textScale, isEnglishLocale, widthBucket, t]);

  // Geometry precompute runs once per result/layout change. Scroll-time
  // selection below is binary, so a 50k-result vine does not rescan these
  // arrays on every animation frame.
  const nodeTops = useMemo(
    () => layouts.filter((l) => l.item.kind === 'node').map((l) => l.top),
    [layouts],
  );
  const bucketLayouts = useMemo(
    () => layouts.filter((layout) => layout.item.kind === 'bucket'),
    [layouts],
  );
  const geometry = useMemo(
    () => buildVineGeometry(nodeTops, MAX_SLOT),
    [nodeTops, MAX_SLOT],
  );
  // Scroll min-height: max(layout total, the geometry's sprig reservation) so
  // the last card always has a full slot below it (a3415d6 tail fix, carried
  // into the layoutItems model) AND the single-prayer sprig tail is preserved.
  const reservedHeight = Math.max(total, geometry.vineHeight);

  // Scoped scroll anchoring (density plan 2026-08-25): when the layout
  // changes UNDER the user mid-scroll — a sync landing, a status change, a
  // reclassification, a slot-table swap — keep the topmost visible item at
  // the same on-screen position by shifting scrollTop by its top-delta.
  // DELIBERATELY SKIPPED when the query/filter changed (a new result set
  // should behave like today: browser-clamped, effectively top-anchored) and
  // at scrollTop <= 0 (nothing to preserve). Scale/rotation shifts ride the
  // same path harmlessly. This reads container scroll state only — never
  // card DOM.
  const prevLayoutsRef = useRef<VineItemLayout[] | null>(null);
  const anchorCtxRef = useRef('');
  useLayoutEffect(() => {
    const prev = prevLayoutsRef.current;
    prevLayoutsRef.current = layouts;
    const ctx = `${searchScopeKey}\0${entryFilter}\0${searchQuery}\0${filter}`;
    const ctxChanged = anchorCtxRef.current !== ctx;
    anchorCtxRef.current = ctx;
    if (!prev || prev === layouts || ctxChanged) return;
    const scrollEl = wrapRef.current?.parentElement;
    if (!scrollEl) return;
    const st = scrollEl.scrollTop;
    if (st <= 0) return;
    const newTops = new Map(layouts.map((l) => [vineItemKey(l.item), l.top]));
    for (const l of prev) {
      if (l.top + l.height <= st) continue; // above the viewport top
      const newTop = newTops.get(vineItemKey(l.item));
      if (newTop === undefined) continue; // item gone — anchor on the next
      const delta = newTop - l.top;
      if (delta !== 0) {
        scrollEl.scrollTop = st + delta;
        setScrollTop(scrollEl.scrollTop);
      }
      break;
    }
  }, [layouts, searchScopeKey, entryFilter, searchQuery, filter]);

  // Reading position (P4, 2026-10-17). Restored once per mount (after a
  // Main <-> People switch or a freeze-recovery remount), before paint, once
  // this exact result set and its width-bucketed layouts exist. A record from
  // another account/auth scope, query or filter never applies: the vine opens
  // at the top. A vanished anchor falls back to the saved raw position. The
  // lookup uses computed layouts only (rule #39); the anchoring effect above
  // absorbs any later re-flow as before.
  const positionTag = `${searchScopeKey}\0${entryFilter}\0${searchQuery}\0${filter}`;
  const positionRestoredRef = useRef(false);
  useLayoutEffect(() => {
    if (positionRestoredRef.current) return;
    const wrap = wrapRef.current;
    const scrollEl = wrap?.parentElement;
    if (!wrap || !scrollEl || wrapWidth === 0) return;
    const record = position.current;
    const saved = record.vine?.tag === positionTag ? record.vine : null;
    if (saved && searchPending) return; // wait for this exact result set
    let target = 0;
    if (saved && layouts.length > 0) {
      const anchor = layouts.find((l) => vineItemKey(l.item) === saved.key);
      // Vine coordinates plus whatever currently sits above the wrap; a
      // sticky label that appears afterwards is absorbed by scroll anchoring.
      target = anchor
        ? anchor.top + saved.offset + (scrollEl.scrollTop - vineScrolled(scrollEl, wrap))
        : saved.rawTop;
    }
    scrollEl.scrollTop = target;
    setScrollTop(scrollEl.scrollTop);
    positionRestoredRef.current = true;
    record.held = false;
  }, [layouts, wrapWidth, searchPending, positionTag, position]);

  // Record while healthy: on every scroll frame (the measurement listener
  // below) and after a result-set or layout change, so the record always
  // describes what is on screen under the current tag.
  // `wrap` is the live element: the empty/searching state renders a different
  // element than the result list, so one captured at mount can be detached.
  const recordInputsRef = useRef<{
    layouts: VineItemLayout[];
    tag: string;
    position: { current: GardenPositionRecord };
    wrap: HTMLDivElement | null;
  }>({ layouts, tag: positionTag, position, wrap: null });
  useLayoutEffect(() => {
    recordInputsRef.current = { layouts, tag: positionTag, position, wrap: wrapRef.current };
  });
  useEffect(() => {
    const wrap = wrapRef.current;
    const scrollEl = wrap?.parentElement;
    if (!wrap || !scrollEl || !positionRestoredRef.current || searchPending) return;
    recordVinePosition(position.current, scrollEl, wrap, layouts, positionTag);
  }, [layouts, positionTag, searchPending, position]);

  // Scroll measurement — wrapRef points at .vineWrap, scroll element
  // is its parent (.canvas). useLayoutEffect-based seed plus rAF-
  // throttled scroll/resize listeners.
  const wrapRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const rafRef = useRef<number | null>(null);
  // Separate from rafRef so the post-mount settle pass and the
  // scroll-throttle rAF never stomp each other (see the settle loop
  // below). Cancelled in the same cleanup.
  const settleRafRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const scrollEl = wrap.parentElement;
    if (!scrollEl) return;

    const update = () => {
      setScrollTop(scrollEl.scrollTop);
      setClientHeight(scrollEl.clientHeight);
      setWrapWidth(wrap.clientWidth);
      rafRef.current = null;
      const { layouts: current, tag, position: record, wrap: liveWrap } = recordInputsRef.current;
      if (positionRestoredRef.current && liveWrap?.isConnected) {
        recordVinePosition(record.current, scrollEl, liveWrap, current, tag);
      }
    };

    // Initial seed before paint so the first render shows the correct
    // window instead of an empty container.
    update();

    // Post-mount settle pass. On a cold open the .canvas height can be
    // 0 or a stale dynamic-viewport value at the layout-effect tick
    // (iOS URL-bar / keyboard timing), so re-measure on rAF until the
    // height is stable across two consecutive non-zero frames (catches
    // the stale-non-zero case, not just a 0 read) or a small frame cap
    // is hit. Measurement only — no geometry recompute here (rule #39).
    let settleFrames = 0;
    let prevSettleHeight = scrollEl.clientHeight;
    const settle = () => {
      update();
      const h = scrollEl.clientHeight;
      const stable = h !== 0 && h === prevSettleHeight;
      prevSettleHeight = h;
      if (!stable && settleFrames++ < 10) {
        settleRafRef.current = requestAnimationFrame(settle);
      } else {
        settleRafRef.current = null;
      }
    };
    settleRafRef.current = requestAnimationFrame(settle);

    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(update);
    };

    // { passive: true } is critical for iOS scroll perf — do not remove.
    scrollEl.addEventListener('scroll', onScroll, { passive: true });

    // Observe BOTH elements: the scroll container (viewport size
    // changes) and the wrap (content height changes when filter/search
    // shrinks the prayer count, which can leave scrollTop past the new
    // max — browser auto-clamps and we re-sync here).
    const ro = new ResizeObserver(() => update());
    ro.observe(scrollEl);
    ro.observe(wrap);

    return () => {
      scrollEl.removeEventListener('scroll', onScroll);
      ro.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (settleRafRef.current != null) cancelAnimationFrame(settleRafRef.current);
      // Last record on unmount (a switch tapped within a frame of scrolling),
      // only while the wrap is still attached. Held during a recovery remount.
      const { layouts: current, tag, position: record, wrap: liveWrap } = recordInputsRef.current;
      if (positionRestoredRef.current && liveWrap?.isConnected) {
        recordVinePosition(record.current, scrollEl, liveWrap, current, tag);
      }
    };
  }, []);

  // Disable the entry-stagger animation on every render except the
  // very first. Without this, every windowed re-render during scroll
  // would replay the 0.5-1.85s stagger as new nodes enter the DOM.
  // ref-based: doesn't trigger re-renders, just gates the className
  // on subsequent renders that fire for OTHER reasons (scroll, filter).
  const hasRenderedRef = useRef(false);
  useEffect(() => {
    hasRenderedRef.current = true;
  }, []);
  const skipAnim = hasRenderedRef.current;

  // Publish only settled results. The parent additionally checks corpus object
  // identity and owner/auth scope before exposing this count or fuzzy notice.
  useEffect(() => {
    if (searchPending) return;
    onResultCount?.(
      filtered.length,
      searchQuery,
      filter,
      isFuzzy,
      isRelated,
      isEnglishLocale,
      prayers,
      searchScopeKey,
    );
  }, [
    filtered.length,
    searchPending,
    searchQuery,
    filter,
    isFuzzy,
    isRelated,
    isEnglishLocale,
    prayers,
    searchScopeKey,
    onResultCount,
  ]);

  if (filtered.length === 0) {
    const emptyKey = searchPending
      ? 'garden.searchSearching'
      : searchQuery.trim()
        ? 'garden.searchNoResults'
        : prayers.length === 0
          ? 'garden.empty'
          : 'garden.noMatch';
    return (
      <div
        ref={wrapRef}
        className={styles.vineWrap}
        data-garden-vine
        data-result-count="0"
        data-search-query={searchQuery}
        data-search-state={searchPending ? 'pending' : 'current'}
        data-fuzzy="false"
        data-related="false"
        data-total-layouts="0"
        data-mounted-nodes="0"
        data-mounted-geometry="0"
      >
        <p className={styles.empty}>{t(emptyKey)}</p>
      </div>
    );
  }

  // Window calculation. clientHeight is 0 until useLayoutEffect runs;
  // on that first synthetic render we render conservatively (top of
  // vine) instead of an empty window so initial paint isn't blank.
  const isMeasured = clientHeight > 0;
  const viewTop = scrollTop;
  const viewBottom = scrollTop + clientHeight;

  const visibleRange = isMeasured
    ? gardenVisibleRange(
        layouts,
        viewTop - VINE_OVERSCAN_PX,
        viewBottom + VINE_OVERSCAN_PX,
      )
    : {
        // Top of the vine, bounded like the pre-measure SVG window below
        // (1600 px) and never more than the former 8 items. The mount layout
        // effect measures before paint, so cards past this bound were created,
        // force-styled and removed again inside the opening tap's task.
        start: 0,
        end: Math.min(8, gardenVisibleRange(layouts, 0, 2 * VINE_OVERSCAN_PX).end),
      };
  const visibleLayouts = layouts.slice(visibleRange.start, visibleRange.end);

  // SVG window — bounds in vineWrap-local coords, content offset by
  // svgTop so the SVG's internal viewBox starts at 0.
  const svgTop = isMeasured
    ? Math.max(0, viewTop - VINE_OVERSCAN_PX)
    : 0;
  const svgBottom = isMeasured
    ? Math.min(reservedHeight, viewBottom + VINE_OVERSCAN_PX)
    : Math.min(reservedHeight, 1600);
  const svgHeight = Math.max(0, svgBottom - svgTop);

  // Slice geometry to the window. Includes a small Y margin so beziers
  // beyond the visible edge are still in the path — prevents a "snap"
  // at the boundary as the user scrolls into a new section.
  const sliceMargin = MAX_SLOT;
  const visibleGeometry = buildVisibleVineGeometry(
    geometry,
    svgTop - sliceMargin,
    svgBottom + sliceMargin,
  );

  // Use a viewBox that maps world-space [0..64] × [svgTop..svgBottom]
  // into the SVG's drawing area, so every geometry value (centerline,
  // knuckle cy, tendril M-y) renders in its natural absolute Y. No
  // per-element offset math required — this is the trick that makes
  // virtualized SVG cheap to maintain.
  const windowedPath = stitchVinePath(visibleGeometry.centerlinePoints, 0);

  // Sticky time anchor: the bucket of the top-most scrolled position. Mirrors
  // RememberStream's stickyYear — walk layouts for the last bucket marker with
  // top <= viewTop, and suppress the pinned label while that marker is itself
  // still on screen (so it never duplicates the in-flow marker).
  let stickyBucket: Bucket | null = null;
  if (showBuckets) {
    const marker = gardenLastAtOrBefore(bucketLayouts, viewTop);
    if (marker && marker.top + marker.height <= viewTop && marker.item.kind === 'bucket') {
      stickyBucket = marker.item.bucket;
    }
  }

  return (
    <>
      {stickyBucket && (
        <div className={styles.stickyBucket} aria-hidden="true">
          {bucketLabelFor(stickyBucket, t, language)}
        </div>
      )}
    <div
      ref={wrapRef}
      className={styles.vineWrap}
      data-garden-vine
      data-result-count={filtered.length}
      data-search-query={searchQuery}
      data-search-state="current"
      data-fuzzy={isFuzzy ? 'true' : 'false'}
      data-related={isRelated ? 'true' : 'false'}
      data-total-layouts={layouts.length}
      data-mounted-nodes={visibleLayouts.filter((layout) => layout.item.kind === 'node').length}
      data-visible-start={visibleRange.start}
      data-visible-end={visibleRange.end}
      data-mounted-geometry={
        visibleGeometry.centerlinePoints.length +
        visibleGeometry.knuckles.length +
        visibleGeometry.tendrils.length
      }
      style={{
        /* Scroll-end clearance for the LAST card (2026-08-26): the scroll
           range must extend past the oldest prayer's card by the iOS
           home-indicator inset + a breath, or its action row (incl. the
           wrapped verse button on wide-wrapper cards) sits flush under
           the indicator at max scroll. Lives in the inline min-height
           calc — env() resolves in inline styles — because the global
           border-box sizing absorbs a CSS padding-bottom INSIDE this
           min-height (the first-attempt fix was inert). */
        minHeight: `calc(${reservedHeight}px + max(env(safe-area-inset-bottom, 0px), 12px) + 24px)`,
      }}
    >
      <svg
        className={styles.vineSvg}
        width="64"
        height={svgHeight}
        viewBox={`0 ${svgTop} 64 ${svgHeight}`}
        style={{ top: svgTop }}
        aria-hidden="true"
      >
        <defs>
          {/* userSpaceOnUse + absolute Y bounds so the fade-in/fade-out
              maps to the FULL vine, not just the visible window. With
              the default objectBoundingBox mode each windowed slice
              would fade internally — visible mid-vine — which would be
              a regression vs. the pre-virtualization look. */}
          <linearGradient
            id="vineGrad"
            gradientUnits="userSpaceOnUse"
            x1="0"
            y1="0"
            x2="0"
            y2={reservedHeight}
          >
            <stop offset="0%" stopColor="rgba(100,70,40,0)" />
            <stop offset="4%" stopColor="rgba(90,65,35,0.55)" />
            <stop offset="50%" stopColor="rgba(80,55,30,0.65)" />
            <stop offset="96%" stopColor="rgba(90,65,35,0.55)" />
            <stop offset="100%" stopColor="rgba(100,70,40,0)" />
          </linearGradient>
        </defs>

        {windowedPath && (
          <>
            <path d={windowedPath} fill="none" stroke="rgba(90,65,35,0.12)" strokeWidth="14" strokeLinecap="round" />
            <path
              d={windowedPath}
              fill="none"
              stroke="url(#vineGrad)"
              strokeWidth="7"
              strokeLinecap="round"
              className={!skipAnim ? styles.vinePathAnim : undefined}
            />
            <path d={windowedPath} fill="none" stroke="rgba(140,105,60,0.25)" strokeWidth="3" strokeLinecap="round" />

            {visibleGeometry.tendrils.map((tendril) => (
              <path
                key={`tendril-${tendril.midY}`}
                d={tendril.d}
                fill="none"
                stroke="rgba(120,80,40,0.5)"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            ))}

            {visibleGeometry.knuckles.map((k) => (
              <g key={`knuckle-${k.y}`} transform={`rotate(${k.angle} ${k.x} ${k.y})`}>
                <ellipse cx={k.x} cy={k.y} rx={k.rx} ry={k.ry} fill="rgba(80,55,30,0.65)" />
                <ellipse
                  cx={k.x - k.rx * 0.28}
                  cy={k.y - k.ry * 0.25}
                  rx={k.rx * 0.38}
                  ry={k.ry * 0.32}
                  fill="rgba(200,165,110,0.28)"
                />
              </g>
            ))}
          </>
        )}
      </svg>

      {visibleLayouts.map((l) => {
        if (l.item.kind === 'bucket') {
          return (
            <div
              key={`bucket-${l.item.bucket.id}`}
              className={styles.bucketMarker}
              style={{ top: l.top, height: l.height }}
            >
              {bucketLabelFor(l.item.bucket, t, language)}
            </div>
          );
        }
        const prayer = l.item.prayer;
        const top = l.top;
        const color = colorFor(prayer);
        const isAlive = statusOf(prayer) === 'Answered' || color === 'gold';
        const isMoving = color === 'gold';
        const isAnswered = statusOf(prayer) === 'Answered';
        const rawWho = whoOf(prayer);
        const displayName = isPraise(prayer) ? t('entry.praiseLabel') : isSelf(rawWho) ? t('people.you') : rawWho;
        const normalSummary = prayer.gardenSummary || cardSummary(prayer);
        const searchMatchKind = matchKindByPrayerId.get(prayer.id);
        const searchContext = searchQuery.trim()
          ? searchMatchKind === 'related'
            ? 'related'
            : prayerSearchContextKind(prayer, searchQuery, normalSummary)
          : 'visible';
        // Never paint hidden AI-derived prose as though it were the user's
        // remembered wording. Authored-only and metadata-only matches get a
        // localized neutral explanation; visible who/summary matches retain
        // the familiar card summary.
        const summary =
          searchContext === 'visible'
            ? normalSummary
            : searchContext === 'authored'
              ? t('garden.searchMatchedAuthored')
              : searchContext === 'related'
                ? t('garden.searchMatchedRelated')
                : t('garden.searchMatchedDetails');
        const statusLabel = isAnswered
          ? t('garden.answered')
          : isMoving
          ? t('garden.godIsMoving')
          : t('garden.stillPraying');
        const anchor = prayer.scriptureAnchor;
        // A verse and a whole chapter each count as one selected unit.
        const scriptureCount = anchor ? (anchor.wholeChapter ? 1 : anchor.verses.length) : 0;
        const scriptureAria = anchor
          ? anchor.wholeChapter
            ? t('garden.scriptureChapterAria')
            : t('garden.scriptureVersesAria', { count: anchor.verses.length })
          : null;
        const Icon = ICON_MAP[color];
        const iconClass = color === 'blue' ? styles.iconSeed : color === 'gold' ? styles.iconSprout : styles.iconLeaf;
        // The glow-wrapper flex width (44/54/66 per color). Fed to CSS as
        // --icon-col so .nodeActionRow can span the FULL card width via a
        // negative margin — the icon column is empty at the row's height,
        // and without the breakout the en 3-button row wraps on gold/green
        // cards (the "Find orphaned on its own row" report, 2026-08-26).
        const iconCol = color === 'green' ? 66 : color === 'gold' ? 54 : 44;
        const daysSinceUpdate = (Date.now() - new Date(prayer.updatedAt).getTime()) / 86400000;
        const isWarm = daysSinceUpdate <= WARM_DAYS;

        // Verse action state (Chunk 2, rule #80) — decided SYNCHRONOUSLY
        // from cheap fields only (never a per-card async resolve, rule
        // #39). isCrisisPrayer is a regex over rawText, run only for the
        // ≤16 visible cards. hasSaved is the same cheap label check the
        // share flow uses. View is NOT isPrivate-gated (rule #27 — a
        // held-close prayer DOES view its saved verses in-app); Find is
        // gated !isPrivate + signed-in + online (the wire call).
        const verseEligible =
          verseLaneOn && prayer.rawText.trim().length > 0 && !isCrisisPrayer(prayer.rawText);
        const hasSavedVerses = (prayer.verseResult?.entries.length ?? 0) > 0;
        const showVerseView = verseEligible && hasSavedVerses;
        const showVerseFind =
          verseEligible && !hasSavedVerses && !prayer.isPrivate && verseFindGloballyOk;
        const verseBusy = verseBusyId === prayer.id;

        return (
          <div
            key={prayer.id}
            className={`${styles.node} ${l.layoutClass === 'compact' ? styles.nodeCompact : ''} ${skipAnim ? styles.nodeInstant : ''} ${isMoving ? styles.nodeMoving : ''} ${isWarm ? styles.nodeWarm : styles.nodeCool}`}
            style={{ top, '--icon-col': `${iconCol}px` } as CSSProperties}
            data-prayer-id={prayer.id}
            data-search-match={searchMatchKind ?? 'browse'}
            data-search-context={searchContext}
          >
            <span className={`${styles.iconWrap} ${styles[`glow_${color}`]} ${isAlive ? styles.alive : ''}`}>
              <Icon className={iconClass} />
            </span>
            <div className={styles.nodeContent}>
              <span className={styles.nodeWho}>
                <span className={styles.nodeWhoText}>{displayName}</span>
              </span>
              <div className={styles.nodeMeta}>
                {!isMoving && !isAnswered && (
                  <span className={styles.statusPraying}>
                    <span className={styles.statusLabel}>{t('garden.stillPraying')}</span>
                  </span>
                )}
                {isMoving && (
                  <span className={styles.statusMoving}>
                    <span className={styles.statusDot} />
                    <span className={styles.statusLabel}>{t('garden.godIsMoving')}</span>
                  </span>
                )}
                {isAnswered && (
                  <span className={styles.statusAnswered}>
                    <span className={styles.statusLabel}>{t('garden.answered')}</span>
                  </span>
                )}
                {anchor ? (
                  <span
                    className={styles.scriptureBadge}
                    role="img"
                    aria-label={scriptureAria ?? undefined}
                  >
                    <svg
                      className={styles.scriptureBadgeBook}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
                      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
                    </svg>
                    <span className={styles.scriptureBadgeNum} aria-hidden="true">
                      {scriptureCount}
                    </span>
                  </span>
                ) : null}
              </div>
              {prayer.isPrivate && (
                <div className={styles.nodePrivateBadge}>
                  <PrivateBadge size="sm" />
                </div>
              )}
              {prayer.pendingTranscriptionId && (
                /* B.2 vine card pending icon. Sits in nodeContent's
                   flow as a metadata badge; it is not a tap target.
                   The corner Open button at the .node's top-right
                   stays untouched.

                   V.B.2: when transcriptionFailed is true, swap to
                   the warm-warning failed badge with an alert glyph
                   and visible "TAP TO REVIEW" caption. Tapping the
                   card's Open button reaches PrayerDetail where the
                   TranscriptionRecoveryBanner offers Type / Retry /
                   Discard. Healthy-pending state below is
                   unchanged. */
                prayer.transcriptionFailed || !enhancedVoiceAllowed() ||
                recordingAttention[prayer.pendingTranscriptionId] ? (
                  <div
                    className={styles.pendingBadgeFailed}
                    aria-label={t(
                      // LC7: a recording not on this device is said so first
                      // (never "audio saved" or "didn't capture").
                      recordingAttention[prayer.pendingTranscriptionId] === 'missing'
                        ? 'voice.missingIconAria'
                        : !enhancedVoiceAllowed()
                        ? 'voice.retainedIconAria'
                        : prayer.transcriptionFailed
                          ? 'voice.failedIconAria'
                          : recordingAttention[prayer.pendingTranscriptionId] === 'mismatch'
                            ? 'voice.mismatchIconAria'
                            : 'voice.unreadableIconAria',
                    )}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    <span className={styles.pendingFailedText}>
                      {t(
                        !enhancedVoiceAllowed() &&
                          recordingAttention[prayer.pendingTranscriptionId] !== 'missing'
                          ? 'voice.retainedBadgeText'
                          : 'voice.failedBadgeText',
                      )}
                    </span>
                  </div>
                ) : (
                  <div
                    className={styles.pendingBadge}
                    aria-label={t('voice.pendingIconAria')}
                  >
                    <span className={styles.pendingSpinner} aria-hidden="true" />
                  </div>
                )
              )}
              <div className={styles.nodeBody}>
                <span className={styles.nodeDate}>{formatShortDateTime(prayer.createdAt)}</span>
                <p className={styles.nodeSummary}>{summary}</p>
              </div>
              <div className={styles.nodeActionRow}>
                {/* Core-content cutover: Add-update is a prayer write, so
                    the affordance (and with it the UpdateSheet open path)
                    is hidden while read-only. Share + the verse actions
                    below are reads and stay. */}
                {!isCoreReadOnly() && (
                <button
                  type="button"
                  className={styles.replyBtn}
                  onClick={() => onReply(prayer)}
                  aria-label={`${t('garden.continue')} — ${displayName}`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  <span className={styles.replyLabel}>{t('garden.continue')}</span>
                </button>
                )}
                <button
                  type="button"
                  className={styles.nodeIconAction}
                  onClick={(e) => { e.stopPropagation(); onAirplaneTap(prayer); }}
                  aria-label={shareAriaForPrayer(prayer, t)}
                >
                  <SendGlyph size={18} />
                  <span className={styles.nodeIconActionLabel}>{t('garden.shareLabel')}</span>
                </button>
                {(showVerseView || showVerseFind) && (
                  <button
                    type="button"
                    className={styles.nodeIconAction}
                    disabled={verseBusy}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (showVerseView) onViewScripture(prayer);
                      else onFindScripture(prayer);
                    }}
                    aria-label={showVerseView ? t('garden.verseViewAria') : t('garden.verseFindAria')}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M2 4.5A2.5 2.5 0 0 1 4.5 2H10a2 2 0 0 1 2 2v16a1.5 1.5 0 0 0-1.5-1.5H4a2 2 0 0 1-2-2V4.5z" />
                      <path d="M22 4.5A2.5 2.5 0 0 0 19.5 2H14a2 2 0 0 0-2 2v16a1.5 1.5 0 0 1 1.5-1.5H20a2 2 0 0 0 2-2V4.5z" />
                    </svg>
                    <span className={styles.nodeIconActionLabel}>
                      {showVerseView ? t('garden.verseView') : t('garden.verseFind')}
                    </span>
                  </button>
                )}
              </div>
            </div>
            <button
              type="button"
              className={styles.nodeOpenBtn}
              onClick={() => onPrayerTap(prayer.id)}
              aria-label={`${displayName}, ${formatShortDateTime(prayer.createdAt)}. ${statusLabel}. ${summary}`}
            >
              <span className={styles.nodeOpenLabel}>{t('garden.openPrayer')}</span>
              <svg
                className={styles.nodeOpenBtnChevron}
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        );
      })}
    </div>
    </>
  );
}

/* ---- People list ---- */

interface PeopleGridProps {
  prayers: Prayer[];
  filter: PeopleFilter;
  searchScopeKey: string;
  /** Garden's reading-position record (P4). */
  position: { current: GardenPositionRecord };
  onOpenPerson?: (who: string) => void;
  /** M2 grid fast-path: open the merge sheet for this row's name. Threaded to
   *  VirtualPersonList, which renders the per-row merge glyph. */
  onOpenMerge?: (who: string) => void;
  t: TFn;
}

function PeopleGrid({
  prayers,
  filter,
  searchScopeKey,
  position,
  onOpenPerson,
  onOpenMerge,
  t,
}: PeopleGridProps) {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const searchIsEmpty = search.trim() === '';
  const activeSearch = searchIsEmpty ? '' : deferredSearch;
  const searchPending = !searchIsEmpty && activeSearch !== search;
  const inputRef = useRef<HTMLInputElement>(null);
  const searchId = useId();
  const searchStatusId = `${searchId}-status`;
  const whoMergeMap = useSettingsStore((s) => s.whoMergeMap);
  const selfLabel = t('people.you');
  const generalLabel = t('people.general');
  const searchIndex = useMemo(
    () =>
      buildPeopleSearchIndex(prayers, whoMergeMap, searchScopeKey, {
        self: selfLabel,
        general: generalLabel,
      }),
    [generalLabel, prayers, searchScopeKey, selfLabel, whoMergeMap],
  );
  const searchResult = useMemo(
    () => searchPeopleIndexWithMeta(searchIndex, activeSearch, filter),
    [activeSearch, filter, searchIndex],
  );
  const visible = searchResult.entries;
  // P4: only the first list this grid mounts (a Main -> People switch or a
  // recovery remount) restores or resets the position; a list remounted by a
  // filter or search change keeps today's in-place behaviour.
  const restoreGateRef = useRef(true);

  const clearSearch = useCallback(() => {
    setSearch('');
    inputRef.current?.focus({ preventScroll: true });
    requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
  }, []);

  // Summary is always across ALL named prayers regardless of the current
  // filter — a calm factual summary of the whole memorial, not a response to
  // the active filter.
  const totalPeople = searchIndex.entries.length;
  const movingCount = searchIndex.movingPrayerCount;

  if (totalPeople === 0) {
    return <p className={styles.empty}>{t('people.empty')}</p>;
  }

  return (
    <div
      className={styles.peopleWrap}
      data-people-search-state={searchPending ? 'pending' : 'current'}
      data-people-search-query={activeSearch}
      data-people-result-count={searchPending ? undefined : visible.length}
      data-people-fuzzy={!searchPending && searchResult.fuzzy ? 'true' : 'false'}
    >
      <p className={styles.peopleSummary}>
        {t('people.summaryPeople', { count: totalPeople })}
        {movingCount > 0 && (
          <> · {t('people.summaryMoving', { count: movingCount })}</>
        )}
      </p>

      <div className={styles.searchWrap} role="search" aria-label={t('people.searchLabel')}>
        <label className={styles.srOnly} htmlFor={searchId}>{t('people.searchLabel')}</label>
        <svg
          className={styles.searchIcon}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          id={searchId}
          ref={inputRef}
          type="search"
          className={styles.searchInput}
          value={search}
          onChange={(event) => setSearch(boundDiscoverySearchInput(event.target.value))}
          onKeyDown={(event) => {
            if (event.key !== 'Escape' || search === '') return;
            event.preventDefault();
            event.stopPropagation();
            clearSearch();
          }}
          placeholder={t('people.searchPlaceholder')}
          aria-describedby={!searchIsEmpty ? searchStatusId : undefined}
          aria-busy={searchPending}
          maxLength={DISCOVERY_SEARCH_MAX_QUERY_LENGTH}
          inputMode="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
        />
        {search && (
          <button
            type="button"
            className={styles.searchClear}
            onClick={clearSearch}
            aria-label={t('people.searchClear')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>
      {!searchIsEmpty && (
        <div
          id={searchStatusId}
          className={styles.searchCount}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {searchPending ? (
            t('people.searchSearching')
          ) : (
            <>
              {searchResult.fuzzy && (
                <span className={styles.searchFuzzyNote}>
                  {t('people.searchFuzzyNote')} ·{' '}
                </span>
              )}
              {t('people.searchCount', { count: visible.length })}
            </>
          )}
        </div>
      )}

      {searchPending ? null : visible.length === 0 ? (
        <p className={styles.empty}>
          {searchIsEmpty ? t('people.filterNoResults') : t('people.searchNoResults')}
        </p>
      ) : (
        <VirtualPersonList
          key={`${searchScopeKey}:${filter}:${searchIsEmpty ? 'browse' : 'search'}:${totalPeople}:${movingCount}:${selfLabel}`}
          entries={visible}
          onOpenPerson={onOpenPerson}
          onOpenMerge={onOpenMerge}
          position={position}
          positionTag={`${searchScopeKey}\0${filter}\0${activeSearch}`}
          restoreGate={restoreGateRef}
          t={t}
        />
      )}
    </div>
  );
}

/**
 * Virtualized person list. Same architecture as VineContent: scroll
 * listener walks up to the nearest overflow-y:auto ancestor, rAF-
 * throttled, ResizeObserver on both scroll container and the list,
 * useLayoutEffect-seed before paint. See core rule #29 for the
 * pattern and guardrails.
 *
 * Item height is bounded by 1-line-clamped names — see
 * `.personCardName` for the CSS that holds this invariant. Pitch
 * scales with the body-text preference via `personRowPitchForScale`.
 */
interface VirtualPersonListProps {
  entries: PeopleArchiveEntry[];
  onOpenPerson?: (who: string) => void;
  onOpenMerge?: (who: string) => void;
  /** P4 reading position: Garden's record, this list's tag, and the
   *  grid's one-shot restore gate. */
  position: { current: GardenPositionRecord };
  positionTag: string;
  restoreGate: { current: boolean };
  t: TFn;
}

function VirtualPersonList({
  entries,
  onOpenPerson,
  onOpenMerge,
  position,
  positionTag,
  restoreGate,
  t,
}: VirtualPersonListProps) {
  const textScale = useSettingsStore((s) => s.textScale);
  const ROW_PITCH = personRowPitchForScale(textScale);

  const listRef = useRef<HTMLUListElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [clientHeight, setClientHeight] = useState(0);
  const [listOffset, setListOffset] = useState(0);
  const rafRef = useRef<number | null>(null);
  // See the VineContent settle pass — same shared scaffold, same
  // cold-mount measurement edge. Separate from rafRef.
  const settleRafRef = useRef<number | null>(null);
  const positionInputsRef = useRef({ position, tag: positionTag, gate: restoreGate });
  useLayoutEffect(() => {
    positionInputsRef.current = { position, tag: positionTag, gate: restoreGate };
  });

  useLayoutEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;
    let scrollEl: HTMLElement | null = listEl.parentElement;
    while (scrollEl && getComputedStyle(scrollEl).overflowY !== 'auto') {
      scrollEl = scrollEl.parentElement;
    }
    if (!scrollEl) return;
    const sEl = scrollEl;

    // P4: the grid's first list restores a matching record before paint, or
    // opens at the top (never the vine's clamped offset). Rows have a fixed
    // pitch and full height from this render, so the value does not clamp.
    const { position: record, tag, gate } = positionInputsRef.current;
    if (gate.current) {
      gate.current = false;
      const saved = record.current.people;
      sEl.scrollTop = saved && saved.tag === tag ? saved.scrollTop : 0;
      record.current.held = false;
    }

    const recordPeople = () => {
      const { position: latest, tag: latestTag } = positionInputsRef.current;
      if (!latest.current.held && scrollsNow(sEl)) {
        latest.current.people = { tag: latestTag, scrollTop: sEl.scrollTop };
      }
    };

    const update = () => {
      const lr = listEl.getBoundingClientRect();
      const sr = sEl.getBoundingClientRect();
      setListOffset(lr.top - sr.top + sEl.scrollTop);
      setScrollTop(sEl.scrollTop);
      setClientHeight(sEl.clientHeight);
      rafRef.current = null;
      recordPeople();
    };

    update();

    // Post-mount settle pass — mirrors VineContent. The grid is almost
    // always reached after .canvas is settled, but a future cold-mount
    // entry path would hit the same first-frame measurement edge, so
    // re-measure on rAF until the height is stable across two
    // consecutive non-zero frames or a small frame cap. Measurement
    // only (rule #39). The .canvas min-height: 0 fix (shared scroll
    // container) covers the structural cause for this surface too.
    let settleFrames = 0;
    let prevSettleHeight = sEl.clientHeight;
    const settle = () => {
      update();
      const h = sEl.clientHeight;
      const stable = h !== 0 && h === prevSettleHeight;
      prevSettleHeight = h;
      if (!stable && settleFrames++ < 10) {
        settleRafRef.current = requestAnimationFrame(settle);
      } else {
        settleRafRef.current = null;
      }
    };
    settleRafRef.current = requestAnimationFrame(settle);

    const onScroll = () => {
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(update);
    };
    sEl.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => update());
    ro.observe(sEl);
    ro.observe(listEl);

    return () => {
      sEl.removeEventListener('scroll', onScroll);
      ro.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      if (settleRafRef.current != null) cancelAnimationFrame(settleRafRef.current);
      recordPeople(); // last record on unmount; held during a recovery remount
    };
  }, []);

  const isMeasured = clientHeight > 0;
  const viewTop = isMeasured ? Math.max(0, scrollTop - listOffset) : 0;
  const viewBottom = viewTop + clientHeight;

  const firstIdx = isMeasured
    ? Math.max(0, Math.floor((viewTop - PEOPLE_OVERSCAN_PX) / ROW_PITCH))
    : 0;
  const lastIdx = isMeasured
    ? Math.min(entries.length, Math.ceil((viewBottom + PEOPLE_OVERSCAN_PX) / ROW_PITCH) + 1)
    : Math.min(entries.length, 12);

  const visibleEntries = entries.slice(firstIdx, lastIdx);
  const totalHeight = entries.length * ROW_PITCH;

  return (
    <ul
      ref={listRef}
      className={styles.personList}
      style={{ position: 'relative', height: totalHeight }}
      data-total-person-rows={entries.length}
      data-mounted-person-rows={visibleEntries.length}
    >
      {visibleEntries.map((entry, j) => {
        const i = firstIdx + j;
        return (
          <li
            key={entry.identityKey}
            className={styles.personListItem}
            style={{ position: 'absolute', top: i * ROW_PITCH, left: 0, right: 0 }}
            data-person-key={entry.identityKey}
          >
            <PersonCard
              entry={entry}
              isYou={isSelf(entry.who)}
              onOpenPerson={onOpenPerson}
              t={t}
            />
            {/* M2: merge glyph as a SIBLING of the card button (never nested -
                PersonCard is itself a button). Eligible rows only (Self/General
                excluded). Absolute overlay -> adds no row height (rule #39). */}
            {/* CR (PC-B rev 9 section 12a): the People-grid merge glyph is
                HIDDEN for the CR window. */}
            {!CR_CONTAINMENT_ACTIVE && onOpenMerge && isAliasCandidateEligible(entry.who) && (
              <button
                type="button"
                className={styles.personMergeBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenMerge(entry.who);
                }}
                aria-label={t('people.mergeRowAria', { name: entry.who })}
              >
                <svg className={styles.personMergeIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

interface PersonCardProps {
  entry: PeopleArchiveEntry;
  isYou?: boolean;
  onOpenPerson?: (who: string) => void;
  t: TFn;
}

function PersonCard({ entry, isYou, onOpenPerson, t }: PersonCardProps) {
  const displayName = entry.displayName;
  const lastDate = entry.last ? formatShortDateTime(entry.last) : null;

  return (
    <button
      type="button"
      className={`${styles.personCard} ${isYou ? styles.youCard : ''}`}
      onClick={() => onOpenPerson?.(entry.who)}
      aria-label={t('people.prayersFor', { name: displayName })}
    >
      <span className={styles.personCardTop}>
        <h3 className={styles.personCardName}>{displayName}</h3>
        {lastDate && (
          <span className={styles.personCardLast}>{lastDate}</span>
        )}
      </span>
      <span className={styles.personCardCount}>
        {t('people.prayerCount', { count: entry.count })}
      </span>
    </button>
  );
}

/* ---- Person view: chronological timeline for one who ---- */

function truncate(text: string, max: number): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return trimmed.slice(0, max - 1).trimEnd() + '…';
}

interface PersonViewProps {
  who: string;
  prayers: Prayer[];
  /** Close the `person` overlay (Back / circle-chevron). NavStack pops it. */
  onClose: () => void;
  /** Timeline prayer tap -> the DISTINCT `person-detail` layer (NOT the
   *  top-level `detail`), so a detail sitting under `person` never collides
   *  (CLAUDE.md rule #78 A3 / README §3.4). */
  onOpenPersonDetail: (id: string) => void;
  onOpenKeepsake?: (who: string) => void;
  /** Coordinator-computed z for the `person` layer root (A3). */
  zIndex?: number;
}

/**
 * Standalone person-profile overlay (A3 extraction, rule #78). Lifted out of
 * Garden's internal view so opening a person is a UNIFORM single PUSH from any
 * entry (Garden vine/grid, PrayerDetail who-name, EvidenceBook person-name) and
 * Back returns to the opener. Reuses Garden.module.css's `.garden` / `.header`
 * / `.close` / `.canvas` shell (the person view always painted inside that
 * shell before; only Garden's vine/grid chrome was hidden). The inline-update
 * sheet is lifted to ONE PersonView-level state + the `person-update` NavStack
 * layer, mirroring Garden's `garden-update` pattern.
 */
export function PersonView({ who, prayers, onClose, onOpenPersonDetail, onOpenKeepsake, zIndex }: PersonViewProps) {
  const { t, i18n } = useTranslation();
  const [keyboardInset, setKeyboardInset] = useState(0);
  // App notice stack: same in-flow spacer as the Garden shell (see Garden).
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();
  // Lifted inline-update state -> ONE `person-update` layer (was per-card
  // sheetOpen). PersonView mounts with this null (closed) so the hook's
  // mount-time double-invoke is a no-op -> StrictMode-safe (§3.2a).
  const [updateForPrayer, setUpdateForPrayer] = useState<Prayer | null>(null);
  const zPersonUpdate = useNavLayer('person-update', updateForPrayer !== null, () => {
    // Ordinary Back preserves the draft. Target cleanup keeps the capture
    // through existing custody; it is never an implicit audio Discard.
    setUpdateForPrayer(null);
  });
  const dismissedPairs = useSettingsStore((s) => s.dismissedAliasPairs);
  const dismissAliasPair = useSettingsStore((s) => s.dismissAliasPair);
  const mergeWhoAliases = useSettingsStore((s) => s.mergeWhoAliases);
  // P1: subscribe to the merge map REACTIVELY so a "Yes" merge re-renders
  // PersonView in place (the suggestion clears, title/list update). Without
  // this subscription a merge persisted but nothing on screen changed.
  const whoMergeMap = useSettingsStore((s) => s.whoMergeMap);

  // Manual merge sheet (rule #30 layer 4, manual entry). ONE NavStack layer
  // above `person`; mounts CLOSED so StrictMode's double-invoke is a no-op
  // (rule #78). The commit is atomic, so onRequestClose is a plain close.
  const [mergeOpen, setMergeOpen] = useState(false);
  const zMerge = useNavLayer('person-merge', mergeOpen, () => setMergeOpen(false));

  // Merge undo-toast (M2: extracted to a shared hook + component, rule #4).
  const {
    toast: mergeToast,
    showMergeToast,
    undo: handleMergeUndo,
    scheduleDismiss: scheduleMergeToastDismiss,
    cancelDismiss: cancelMergeToastDismiss,
  } = useMergeUndoToast();

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const diff = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      setKeyboardInset(diff > 100 ? diff : 0);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  // P1: the canonical identity spine. After a merge, `who` (the raw prop)
  // stays the alias the view was opened with, but `effectiveWho` resolves to
  // the chosen canonical, so the prayer list, title, suggestion, and keepsake
  // handoff all follow the merge. Recomputes when the reactive map changes.
  const effectiveWho = useMemo(() => canonicalizeWho(who, whoMergeMap), [who, whoMergeMap]);

  const personPrayers = useMemo(() => prayersForWho(prayers, effectiveWho), [prayers, effectiveWho]);
  const displayName = isSelf(effectiveWho)
    ? t('people.you')
    : effectiveWho === 'General'
    ? t('people.general')
    : effectiveWho;
  const title = isSelf(effectiveWho)
    ? t('people.yourPrayers')
    : effectiveWho === 'General'
    ? t('people.generalPrayers')
    : t('people.prayersFor', { name: displayName });

  const chronological = useMemo(
    () => [...personPrayers].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    ),
    [personPrayers],
  );

  // Variable-height drilldown per CLAUDE.md rule #20 amendment. Default
  // shows the most-recent LIST_HARD_CAP entries (the tail of the
  // oldest-first array); the button at the head of the timeline reveals
  // the older ones inline. Key the expansion to `who` so opening a
  // different person cannot carry the prior expanded state.
  const [expandedTimelineFor, setExpandedTimelineFor] = useState<string | null>(null);
  const showAllOlderTimeline = expandedTimelineFor === who;
  const olderCount = Math.max(0, chronological.length - LIST_HARD_CAP);
  const visibleChronological = (olderCount === 0 || showAllOlderTimeline)
    ? chronological
    : chronological.slice(olderCount);

  const firstPrayerDate = chronological[0]?.createdAt ?? null;
  const totalCount = personPrayers.length;
  const themes = useMemo(() => extractThemes(personPrayers, 3), [personPrayers]);

  // On-demand theme clusters via Grok. Fire-and-forget: the token-based
  // `themes` above renders immediately as a graceful fallback while the
  // Grok call resolves; on success, clusters replace the text line;
  // on failure, the text line stays. Private prayers are pre-filtered
  // for UX, then the rule #90 wire boundary rechecks the durable owner,
  // every exact revision, and public privacy state after auth resolves.
  const safeThemePrayers = useMemo(
    () => personPrayers.filter((prayer) => !prayer.isPrivate).slice(0, 100),
    [personPrayers],
  );
  const themeInput = useMemo(() => safeThemePrayers.map(prayerThemeInput), [safeThemePrayers]);
  const themeLanguage = (i18n.language ?? 'en').slice(0, 2);
  const themeRequestKey = themeInputKey(themeInput, themeLanguage);
  const [clusterResult, setClusterResult] = useState<{
    requestKey: string;
    clusters: ThemeCluster[];
  } | null>(null);
  const clusters = safeThemePrayers.length < 2
    ? []
    : clusterResult?.requestKey === themeRequestKey
      ? clusterResult.clusters
      : null;
  useEffect(() => {
    if (safeThemePrayers.length < 2) return;
    let cancelled = false;
    const binding = captureOutboundOwnerBinding();
    const stillCurrent = () => !cancelled && binding !== null && outboundOwnerStillCurrent(binding);
    generateThemeClusters(themeInput, themeLanguage)
      .then((c) => {
        if (stillCurrent()) setClusterResult({ requestKey: themeRequestKey, clusters: c });
      })
      .catch(() => {
        if (stillCurrent()) setClusterResult({ requestKey: themeRequestKey, clusters: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [safeThemePrayers, themeInput, themeLanguage, themeRequestKey]);

  // Last-resort fuzzy suggestion for the rare residual duplicate that
  // slipped past the parser's existingPeople context and the read-time
  // normalizer. Never prompts the user more than once per pair — a "No,
  // keep separate" tap writes the pair key to dismissedAliasPairs
  // permanently. "Yes" routes through mergeWhoAliases which the rest of
  // the app honors via canonicalizeWho.
  const aliasSuggestion = useMemo(
    // CR (PC-B rev 9 section 12a): the alias-suggestion row is HIDDEN for
    // the CR window. With no active assertions there is nothing to suggest
    // against and nothing to dismiss; leaving the row visible with an
    // inert dismiss would make it re-fire forever. The pre-CR gate is
    // retained below for S5's replacement surface.
    // Guard AND target both key off effectiveWho (not raw `who`) so a
    // pre-existing merge that maps a raw person into a sentinel can never
    // reopen a suggestion on it (P1 / LOW 1).
    () => (CR_CONTAINMENT_ACTIVE || isSelf(effectiveWho) || effectiveWho === 'General'
      ? null
      : findAliasCandidate(effectiveWho, prayers, dismissedPairs)),
    [effectiveWho, prayers, dismissedPairs],
  );
  const handleAliasYes = () => {
    if (!aliasSuggestion) return;
    const prevMap = whoMergeMap; // snapshot BEFORE the merge, for Undo
    // Merge this person INTO the candidate (the canonical form the user is
    // confirming). effectiveWho is the current identity, so if `who` was
    // itself already an alias, we merge from its canonical form.
    mergeWhoAliases([effectiveWho], aliasSuggestion.candidate);
    showMergeToast(effectiveWho, aliasSuggestion.candidate, prevMap);
  };
  const handleMergeDone = ({ prevMap, keptName, absorbedName }: MergeResult) => {
    showMergeToast(absorbedName, keptName, prevMap);
    setMergeOpen(false);
  };
  const handleAliasNo = () => {
    if (!aliasSuggestion) return;
    dismissAliasPair(aliasPairKey(effectiveWho, aliasSuggestion.candidate));
  };

  return (
    <div className={styles.garden} style={{ zIndex }} role="dialog" aria-modal="true" aria-label={title}>
      <header className={styles.header}>
        <button type="button" className={styles.close} onClick={onClose} aria-label={t('nav.back')}>
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="14 6 8 12 14 18" />
          </svg>
        </button>
      </header>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      <div className={styles.canvas}>
        {personPrayers.length === 0 ? (
          <div className={styles.personView}>
            <h1 className={styles.personViewTitle}>{title}</h1>
            <p className={styles.empty}>{t('garden.noMatch')}</p>
          </div>
        ) : (
          <div
            className={styles.personView}
            style={keyboardInset > 0 ? { paddingBottom: `${keyboardInset + 24}px` } : undefined}
          >
      <header className={styles.personHero}>
        <h1 className={styles.personViewTitle}>{title}</h1>

        {aliasSuggestion && (
          <div className={styles.aliasSuggest}>
            <p className={styles.aliasSuggestPrompt}>
              {t('people.sameAsSuggest', { candidate: aliasSuggestion.candidate })}
            </p>
            <p className={styles.aliasSuggestHint}>
              {t('people.sameAsHint', {
                candidate: aliasSuggestion.candidate,
                count: aliasSuggestion.count,
              })}
            </p>
            <div className={styles.aliasSuggestActions}>
              <button type="button" className={styles.aliasYes} onClick={handleAliasYes}>
                {t('people.sameAsYes')}
              </button>
              <button type="button" className={styles.aliasNo} onClick={handleAliasNo}>
                {t('people.sameAsNo')}
              </button>
            </div>
          </div>
        )}

        {firstPrayerDate && (
          <p className={styles.personSubtitle}>
            {t('people.since', { date: formatShortDate(firstPrayerDate) })}
          </p>
        )}

        {/* CR + PC-B section 11: General is a navigation bucket, not a
            person — the keepsake CTA is HIDDEN on the General profile so
            the client no longer offers a book the server deliberately
            rejects. */}
        {/* Core-content cutover: KEEPSAKES_ENABLED gates the CTA even
            when a caller still passes onOpenKeepsake, so the client
            never offers a book the server answers with a 503. */}
        {KEEPSAKES_ENABLED && onOpenKeepsake && totalCount > 0 && effectiveWho !== 'General' && (
          <button
            type="button"
            className={styles.keepsakeCta}
            onClick={() => onOpenKeepsake(effectiveWho)}
          >
            <span className={styles.keepsakeCtaTitle}>
              {t('people.keepsakeCta', { count: totalCount })}
            </span>
            <svg className={styles.keepsakeCtaArrow} width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        )}

        {/* CR (PC-B rev 9 section 12a): the manual-merge disclosure row is
            HIDDEN for the CR window; the merge sheet mount below is gated
            with it. */}
        {!CR_CONTAINMENT_ACTIVE && !isSelf(effectiveWho) && effectiveWho !== 'General' && (
          <button
            type="button"
            className={styles.mergeAction}
            onClick={() => setMergeOpen(true)}
          >
            <svg className={styles.mergeActionIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <span className={styles.mergeActionLabel}>{t('people.mergeAction')}</span>
            <svg className={styles.mergeActionChevron} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </header>

      {clusters && clusters.length > 0 ? (
        <ul className={styles.themeClusters} aria-label={t('people.themesAria')}>
          {clusters.map((c, i) => (
            <li key={i} className={styles.themePill}>
              <span className={styles.themeLabel}>{c.label}</span>
              {c.span && <span className={styles.themeSpan}>{c.span}</span>}
            </li>
          ))}
        </ul>
      ) : themes.length > 0 ? (
        <p className={styles.personThemes}>
          {t('people.personThemes', { themes: themes.join(', ') })}
        </p>
      ) : null}

      {olderCount > 0 && !showAllOlderTimeline && (
        <button
          type="button"
          className={styles.drilldownBtn}
          onClick={() => setExpandedTimelineFor(who)}
          aria-label={t('nav.showAllEarlierAria', { count: olderCount })}
        >
          {t('nav.showAllEarlier', { count: olderCount })}
        </button>
      )}

      <ol className={styles.personTimeline}>
        {visibleChronological.map((p) => (
          <li key={p.id} className={styles.timelineEntry}>
            <span className={styles.timelineDate}>{formatFullDateTime(p.createdAt)}</span>
            <PersonTimelineCard
              prayer={p}
              onPrayerTap={onOpenPersonDetail}
              onOpenUpdate={setUpdateForPrayer}
              t={t}
            />
          </li>
        ))}
      </ol>
          </div>
        )}
      </div>

      {/* Cutover: never open the composer, even from a stale state. */}
      {updateForPrayer && !isCoreReadOnly() && (
        <UpdateSheet
          prayer={updateForPrayer}
          variant="update"
          voiceTargetPrefix="person-update"
          onClose={() => setUpdateForPrayer(null)}
          zIndex={zPersonUpdate}
        />
      )}

      {!CR_CONTAINMENT_ACTIVE && mergeOpen && (
        <MergePersonSheet
          subjectWho={effectiveWho}
          prayers={prayers}
          onClose={() => setMergeOpen(false)}
          onMerged={handleMergeDone}
          zIndex={zMerge}
        />
      )}

      {mergeToast && (
        <MergeUndoToast
          toast={mergeToast}
          onUndo={handleMergeUndo}
          onPauseDismiss={cancelMergeToastDismiss}
          onResumeDismiss={scheduleMergeToastDismiss}
        />
      )}
    </div>
  );
}

interface PersonTimelineCardProps {
  prayer: Prayer;
  onPrayerTap: (id: string) => void;
  onOpenUpdate: (prayer: Prayer) => void;
  t: TFn;
}

function PersonTimelineCard({ prayer, onPrayerTap, onOpenUpdate, t }: PersonTimelineCardProps) {
  const color = colorFor(prayer);
  const Icon = ICON_MAP[color];
  const gardenSummary = prayer.gardenSummary?.trim();
  const history = prayer.progressHistory ?? [];
  const latestUpdate = history[0];
  const rawExcerpt = truncate(prayer.rawText, 200);
  const updateExcerpt = latestUpdate ? truncate(latestUpdate.note, 200) : null;
  const status = statusOf(prayer);
  const statusLabel = status === 'Answered'
    ? t('garden.answered')
    : color === 'gold'
    ? t('garden.godIsMoving')
    : t('garden.stillPraying');
  const statusClass =
    status === 'Answered' ? styles.statusAnswered :
    color === 'gold' ? styles.statusMoving :
    styles.statusPraying;

  return (
    <div className={`${styles.timelineCard} ${styles[`timelineCard_${color}`]}`}>
      {prayer.isPrivate && (
        <div className={styles.timelineCardPrivateBadge}>
          <PrivateBadge size="sm" />
        </div>
      )}
      <button
        type="button"
        className={styles.timelineCardTap}
        onClick={() => onPrayerTap(prayer.id)}
        aria-label={`${formatFullDate(prayer.createdAt)} — ${rawExcerpt}`}
      >
        <span className={`${styles.timelineCardIcon} ${styles[`timelineCardIcon_${color}`]}`}>
          <Icon className={styles.timelineCardIconSvg} />
        </span>
        <span className={styles.timelineCardBody}>
          <span className={styles.timelineCardSummary}>{t(isPraise(prayer) ? 'entry.praiseLabel' : 'entry.prayerLabel')}</span>
          {gardenSummary && <span className={styles.timelineCardSummary}>{gardenSummary}</span>}
          <span className={styles.timelineCardExcerpt}>{rawExcerpt}</span>
          {updateExcerpt && latestUpdate && (
            <span className={styles.timelineCardUpdate}>
              <span className={styles.timelineCardUpdateDate}>
                {formatShortDateTime(latestUpdate.date)}
              </span>
              <span className={styles.timelineCardUpdateNote}>{updateExcerpt}</span>
            </span>
          )}
          <span className={`${styles.timelineStatusPill} ${statusClass}`}>
            <span className={styles.statusLabel}>{statusLabel}</span>
          </span>
        </span>
      </button>

      <PersonInlineUpdate prayer={prayer} onOpenUpdate={onOpenUpdate} t={t} />
    </div>
  );
}

interface PersonInlineUpdateProps {
  prayer: Prayer;
  onOpenUpdate: (prayer: Prayer) => void;
  t: TFn;
}

// Thin button only — the UpdateSheet is lifted to ONE PersonView-level mount
// (the `person-update` layer, A3). The button asks PersonView to open it for
// this prayer.
function PersonInlineUpdate({ prayer, onOpenUpdate, t }: PersonInlineUpdateProps) {
  // Core-content cutover: same gate as the vine card's Add-update.
  if (isCoreReadOnly()) return null;
  return (
    <button
      type="button"
      className={styles.personInlineCollapsed}
      onClick={() => onOpenUpdate(prayer)}
      aria-label={t('detail.addUpdate')}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      <span>{t('detail.addUpdate')}</span>
    </button>
  );
}

/* ---- Bottom sheet (quick inline update) ---- */

interface GardenUpdateSheetProps {
  prayer: Prayer;
  onClose: () => void;
  zIndex?: number;
}

function GardenUpdateSheet({ prayer, onClose, zIndex }: GardenUpdateSheetProps) {
  return (
    <UpdateSheet
      prayer={prayer}
      variant="update"
      voiceTargetPrefix="garden-update"
      onClose={onClose}
      zIndex={zIndex}
    />
  );
}
